/*
 * Tools for creating TypeGuards.
 */
// primitive type guards
const notNull = (u) => u !== null;
const isNull = (u) => u === null;
const isString = (u) => typeof u === 'string';
const isNumber = (u) => typeof u === 'number';
const isOptional = (guard) => (u) => u === undefined || guard(u);
const isArrayOf = (guard) => (u) => Array.isArray(u) && u.every(guard);
/** TypeGuard for discriminated unions of strings */
const isOneOf = (list) => (u) => !!list.find((i) => i === u);
/** TypeGuard for fixed values */
const isFixed = (value) => (u) => u === value;
// type guard operators
/** TypeGuard for discriminated unions of objects
 *
 * Does not currently infer the array types, so `T` needs to be provided
 */
const isEither = (list) => (u) => !!list.find((g) => g(u));
const isNullable = (guard) => (u) => {
    if (u === null) {
        return true;
    }
    const res = guard(u);
    return typeof res === 'boolean' && res;
};
/** TypeGuard for an object. Needs to have one Guard for every attribute */
const typeGuard = (guards) => (u) => {
    return Object.entries(guards).every(([key, guard]) => {
        if (!u) {
            return false;
        }
        const value = u[key];
        if (!guard(value)) {
            return false;
        }
        return true;
    });
};
/**
 * TypeAssertion for an object. Needs to have one Guard or TypeAssertion for every attribute.
 * Will throw an error if the property is of the wrong type.
 */
// TODO: refactor the typeGuard function to work as a typeAssertion when called by a typeAssertion
function typeAssertion(guards) {
    return function assertGuard(u) {
        // for function Guards like `isEither`
        if (typeof guards === 'function') {
            const res = guards(u);
            if (typeof res === 'boolean' && !res) {
                throw new Error('failed-typeguard');
            }
            return;
        }
        // default with a Guard object
        Object.entries(guards).forEach(([key, guard]) => {
            if (!u) {
                throw new Error(`Can't read property '${key}' of '${u}'`);
            }
            const value = u[key];
            try {
                const res = guard(value);
                if (typeof res === 'boolean' && !res) {
                    throw new Error('failed-typeguard');
                }
            }
            catch (e) {
                // TODO: write or install a nested error object
                throw new Error(`Value '${value}' has the wrong type for property '${key}':${'\n'}${e && e.message}`);
            }
        });
    };
}

const questionTypeFamilies = [
    'PRESENTATION',
    'MULTIPLE_CHOICE',
    'OPEN_ENDED',
    'RATING_SCALE',
    'MATRIX',
    'RANKING',
    'SLIDER',
    'DATETIME',
    'CONTACT_INFO',
    'CLICK_MAP',
    'FILE_UPLOAD',
];
const questionTypeRequiredPredicateTypes = [
    'ALL',
    'AT_LEAST',
    'AT_MOST',
    'EXACTLY',
    'RANGE',
    // 'REQUIRED_ANSWERS_LIST',
];
const questionTypeVariants = [
    'TEXT',
    'IMAGE',
    'SINGLE_LINE',
    'ESSAY',
    'MANY_FIELDS',
    'SINGLE_ANSWER_RADIO',
    'SINGLE_ANSWER_SYMBOL',
    'SINGLE_ANSWER_MENU',
    'MANY_ANSWERS_CHECKBOX',
    'MENU',
    'SINGLE_SLIDER',
    'DATE',
    'TIME',
    'DATETIME',
    'MANY_FIELDS',
    'SINGLE_CLICK',
    'RATING',
    'FILE_UPLOAD',
];
const questionTypeLayoutPlacements = [
    'NEW_ROW',
    'RIGHT_ALIGNED',
];
const questionTypeValidationTypes = [
    'SUM',
    'TEXT_LENGTH',
    'EMAIL',
    'INTEGER',
    'DECIMAL',
    'DATE_US',
    'DATE_INTL',
    'FILE_FORMAT',
];
const questionTypeIds = [
    'single_textbox',
    'comment_box',
];
const questionTypeSortingKinds = [
    'FLIP',
    'RANDOM',
    'TEXT_ASC',
    'TEXT_DESC',
];
const questionTypeSkipLogicTypes = [
    'QUESTION_CONDITIONAL_COL',
    'QUESTION_CONDITIONAL_ROW',
];
const questionTypeSkipLogicDestinationTypes = [
    'DISQUALIFY_RESPONDENT',
    'END_SURVEY',
    'SKIP_TO_PAGE',
];
const questionTypeMultipleChoiceLayouts = [
    'VERTICAL',
    'THREE_COLUMNS',
    'TWO_COLUMNS',
    'HORIZONTAL',
];
const SENTIMENTS = [
    'positive',
    'negative',
    'neutral',
    'unknown',
];
/** Possible icons for Star-Rating */
const ratingScaleSymbols = [
    'THUMB',
    'STAR',
    'HEART',
    'SMILEY',
];
/** Possible values for relative width (in percentage) */
const relativeWidthOptions = [
    10,
    20,
    30,
    40,
    50,
    60,
    70,
    80,
    90,
    100,
];
/** Possible values for fixed width (in pixel) */
const fixedWidthOptions = [
    100,
    200,
    300,
    400,
    500,
    600,
    700,
    800,
];

// Base Types
// Question Guard Helpers
const isQuestionError = typeGuard({
    questionId: isNullable(isString),
    detail: isString,
    type: isOneOf([
        ...questionTypeValidationTypes,
        'REQUIRED',
    ]),
});
/** Guard for minimal information needed to determine the type of a Question */
const baseQuestionGuards = (typeId, family, variant) => ({
    typeId: isFixed(typeId),
    variant: isFixed(variant),
    family: isFixed(family),
    id: isString,
});
/**
 * a very basic typeguard for the common fields for a Question Type.
 * should receive parameters for the actual ID, FAMILY and SUBTYPE of the
 * Question Type to avoid Typescript errors. Question types can use this function
 * to help build their own typeGuards
 */
const isQuestionType = (typeId, family, variant) => typeGuard(baseQuestionGuards(typeId, family, variant));

// Mappings shared by multiple QuestionTypes in the RespondentSurvey Capability
// to convert part of their external to their internal QuestionType representation
/**
 * Mapping external to internal QuestionType properties shared by all QuestionTypes
 * in the RespondentSurvey Capability
 */
function respondentSurveyMapping(externalQuestion) {
    const { position: { label: questionNumber }, heading: { default: { text: title }, }, } = externalQuestion;
    return {
        questionNumber,
        title,
    };
}

// Mappings/Types for ImageChoice shared by all Capabilities
/**
 * Returns fields for ImageChoice's internal QuestionType type
 * that are shared across all Capabilities
 */
const imageChoiceCrossCapabilityMapping = ({ id, variant, }) => ({
    typeId: 'image_choice',
    family: 'MULTIPLE_CHOICE',
    variant: variant,
    id,
});

// Mappings to convert ImageChoice's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const imageChoiceRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { required, answersImage, answerNA } = q;
    return {
        ...respondentSurveyMapping(q),
        ...imageChoiceCrossCapabilityMapping(q),
        required: required ?? undefined,
        answersImage,
        answerNoneOfTheAbove: answerNA ?? undefined,
    };
};

// Mappings shared by multiple QuestionTypes in the RespondentQuizResults Capability
// to convert part of their external to their internal QuestionType representation
/**
 * Mapping external to internal QuestionType properties shared by all QuestionTypes
 * in the RespondentQuizResults Capability
 */
function respondentQuizResultsMapping(externalQuestion) {
    const { quizResults, position: { label: questionNumber }, heading: { default: { text: title }, }, } = externalQuestion;
    return {
        quizResults: quizResults || undefined,
        questionNumber,
        title,
    };
}

// Mappings to convert ImageChoice's external to it's internal QuestionType representation
// in the RespondentQuizResults Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const imageChoiceRespondentQuizResultsMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { answerNA, answersImage } = q;
    return {
        ...respondentQuizResultsMapping(q),
        ...imageChoiceCrossCapabilityMapping(q),
        answersImage,
        answerNoneOfTheAbove: answerNA ?? null, // remove `null` as possible type
    };
};

// Mappings shared by multiple QuestionTypes in the AggregateResponseDashboard Capability
// to convert part of their external to their internal QuestionType representation
/**
 * Mapping external to internal QuestionType properties shared by all QuestionTypes
 * in the AggregateResponseDashboard Capability
 */
function aggregateResponseDashboardMapping(externalQuestion) {
    const { dashboardType, filterCount, selectedCount, skippedCount, answeredCount, responseData, aggregateResponse, heading: { default: { text: title }, }, } = externalQuestion;
    return {
        dashboardType,
        filterCount,
        answeredCount,
        selectedCount,
        skippedCount,
        responseData,
        aggregateResponse,
        title,
    };
}

// Mappings to convert ImageChoice's external to it's internal QuestionType representation
// in the AggregateResponseDashboard Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const imageChoiceAggregateResponseDashboardMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...imageChoiceCrossCapabilityMapping(q),
        ...aggregateResponseDashboardMapping(q),
    };
};

// Mappings shared by multiple QuestionTypes in the AggregateResponseCustomizableSolutions Capability
// to convert part of their external to their internal QuestionType representation
/**
 * Mapping external to internal QuestionType properties shared by all QuestionTypes
 * in the AggregateResponseCustomizableSolutions Capability
 */
function aggregateResponseCustomizableSolutionsMapping(externalQuestion) {
    const { displayNumber: questionNumber, responseData, textResponseData, aggregateResponse, heading: { default: { text: title }, }, } = externalQuestion;
    return {
        questionNumber,
        responseData,
        textResponseData,
        aggregateResponse,
        title,
    };
}

// Mappings to convert ImageChoice's external to it's internal QuestionType representation
// in the AggregateResponseCustomizableSolutions Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const imageChoiceAggregateResponseCustomizableSolutionsMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...imageChoiceCrossCapabilityMapping(q),
        ...aggregateResponseCustomizableSolutionsMapping(q),
    };
};

/** Maps `answerOther` field for QuizResults capability */
const mapRespondentQuizResultsAnswerOther = (answerOther) => {
    if (!answerOther) {
        return null;
    }
    let base;
    if (answerOther.type === 'ANSWER') {
        const addRequiredErrorText = answerOther.requiredErrorText !== null &&
            answerOther.requiredErrorText !== undefined;
        base = {
            type: 'ANSWER',
            ...(addRequiredErrorText
                ? { requiredErrorText: answerOther.requiredErrorText }
                : {}),
        };
    }
    else {
        base = { type: answerOther.type };
    }
    return {
        ...base,
        id: answerOther.id,
        label: answerOther.label,
        visible: answerOther.visible,
        linesCount: answerOther.layout.linesCount,
        charsCount: answerOther.layout.charsCount,
    };
};
/** Splits/Maps external `answerOther` to the internal `answerOther` and `answerComment` */
const mapRespondentSurveyAnswerOther = (answerOther) => {
    if (!answerOther) {
        return {};
    }
    const shared = {
        id: answerOther.id,
        label: answerOther.label,
        visible: answerOther.visible,
        linesCount: answerOther.layout.linesCount,
        charsCount: answerOther.layout.charsCount,
        // custom attributes for `other` validation
        touched: false,
        value: '',
        selected: false,
    };
    if (answerOther.type === 'ANSWER') {
        const requiredErrorText = answerOther.requiredErrorText ?? undefined;
        return {
            answerOther: {
                type: 'ANSWER',
                ...(requiredErrorText !== undefined ? { requiredErrorText } : {}),
                ...shared,
            },
        };
    }
    return {
        answerComment: {
            type: answerOther.type,
            ...shared,
        },
    };
};
/**
 * Maps `QuestionAnswerRequired` to more concrete `QuestionAnswerRequiredAll` type
 * and removes `min` and `max` properties */
const mapRespondentSurveyRequiredAll = (answerOther) => {
    if (!answerOther || answerOther.type !== 'ALL') {
        return undefined;
    }
    return {
        type: 'ALL',
        errorMessage: answerOther.errorMessage,
    };
};
const mapRespondentSurveyRequiredAtLeast = (answerOther) => {
    if (!answerOther || answerOther.type !== 'AT_LEAST') {
        return undefined;
    }
    return {
        type: 'AT_LEAST',
        min: answerOther.min ?? 1,
        errorMessage: answerOther.errorMessage,
    };
};
const fillSpacing = (spacing) => spacing
    ? {
        top: spacing.top ?? 0,
        bottom: spacing.bottom ?? 0,
        left: spacing.left ?? 0,
        right: spacing.right ?? 0,
    }
    : undefined;

// Mappings/Types for CheckBox shared by all Capabilities
/**
 * Returns fields for CheckBox's internal QuestionType type
 * that are shared across all Capabilities
 */
const checkBoxCrossCapabilityMapping = ({ id, }) => ({
    typeId: 'check_box',
    family: 'MULTIPLE_CHOICE',
    variant: 'MANY_ANSWERS_CHECKBOX',
    id,
});

// Mappings to convert CheckBox's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const checkBoxRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { layout, validation, required, answers, answerOther, answerNA } = q;
    return {
        ...respondentSurveyMapping(q),
        ...checkBoxCrossCapabilityMapping(q),
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        required: required ?? undefined,
        validation: validation ?? undefined,
        answers,
        answerNoneOfTheAbove: answerNA ?? undefined,
        ...mapRespondentSurveyAnswerOther(answerOther),
    };
};

// Mappings to convert CheckBox's external to it's internal QuestionType representation
// in the RespondentQuizResults Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const checkBoxRespondentQuizResultsMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { layout, answerOther, answerNA } = q;
    return {
        ...respondentQuizResultsMapping(q),
        ...checkBoxCrossCapabilityMapping(q),
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        answerOther: mapRespondentQuizResultsAnswerOther(answerOther),
        answerNoneOfTheAbove: answerNA ?? null, // remove `null` as possible type
    };
};

// Mappings to convert CheckBox's external to it's internal QuestionType representation
// in the AggregateResponseDashboard Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const checkBoxAggregateResponseDashboardMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...checkBoxCrossCapabilityMapping(q),
        ...aggregateResponseDashboardMapping(q),
    };
};

// Mappings to convert CheckBox's external to it's internal QuestionType representation
// in the AggregateResponseCustomizableSolutions Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const checkBoxAggregateResponseCustomizableSolutionsMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...checkBoxCrossCapabilityMapping(q),
        ...aggregateResponseCustomizableSolutionsMapping(q),
    };
};

// Mappings/Types for Dropdown shared by all Capabilities
/**
 * Returns fields for Dropdown's internal QuestionType type
 * that are shared across all Capabilities
 */
const dropdownCrossCapabilityMapping = ({ id, }) => ({
    typeId: 'dropdown',
    family: 'MULTIPLE_CHOICE',
    variant: 'SINGLE_ANSWER_MENU',
    id,
});

// Mappings to convert Dropdown's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const dropdownRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { layout, validation, required, answers, answerOther } = q;
    return {
        ...respondentSurveyMapping(q),
        ...dropdownCrossCapabilityMapping(q),
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        required: mapRespondentSurveyRequiredAll(required),
        validation: validation ?? undefined,
        answers,
        ...mapRespondentSurveyAnswerOther(answerOther),
    };
};

// Mappings to convert Dropdown's external to it's internal QuestionType representation
// in the RespondentQuizResults Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const dropdownRespondentQuizResultsMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { layout, answerOther } = q;
    return {
        ...respondentQuizResultsMapping(q),
        ...dropdownCrossCapabilityMapping(q),
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        answerOther: mapRespondentQuizResultsAnswerOther(answerOther),
    };
};

// Mappings to convert Dropdown's external to it's internal QuestionType representation
// in the AggregateResponseDashboard Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const dropdownAggregateResponseDashboardMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...dropdownCrossCapabilityMapping(q),
        ...aggregateResponseDashboardMapping(q),
    };
};

// Mappings to convert Dropdown's external to it's internal QuestionType representation
// in the AggregateResponseCustomizableSolutions Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const dropdownAggregateResponseCustomizableSolutionsMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...dropdownCrossCapabilityMapping(q),
        ...aggregateResponseCustomizableSolutionsMapping(q),
    };
};

// Mappings/Types for CommentBox shared by all Capabilities
/**
 * Returns fields for CommentBox's internal QuestionType type
 * that are shared across all Capabilities
 */
const commentBoxCrossCapabilityMapping = ({ id, }) => ({
    typeId: 'comment_box',
    family: 'OPEN_ENDED',
    variant: 'ESSAY',
    id,
});

// Mappings to convert CommentBox's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const commentBoxRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { layout, validation, required, fieldSet } = q;
    return {
        ...respondentSurveyMapping(q),
        ...commentBoxCrossCapabilityMapping(q),
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        fieldSet,
        // can only be required `all` of OpenEnded questions
        required: mapRespondentSurveyRequiredAll(required),
        validation: validation ?? undefined,
    };
};

// Mappings to convert CommentBox's external to it's internal QuestionType representation
// in the AggregateResponseDashboard Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const commentBoxAggregateResponseDashboardMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...commentBoxCrossCapabilityMapping(q),
        ...aggregateResponseDashboardMapping(q),
    };
};

// Mappings to convert CommentBox's external to it's internal QuestionType representation
// in the AggregateResponseCustomizableSolutions Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const commentBoxAggregateResponseCustomizableSolutionsMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...commentBoxCrossCapabilityMapping(q),
        ...aggregateResponseCustomizableSolutionsMapping(q),
    };
};

// Mappings/Types for SingleTextbox shared by all Capabilities
/**
 * Returns fields for SingleTextbox's internal QuestionType type
 * that are shared across all Capabilities
 */
const singleTextboxCrossCapabilityMapping = ({ id, }) => ({
    typeId: 'single_textbox',
    family: 'OPEN_ENDED',
    variant: 'SINGLE_LINE',
    id,
});

// Mappings to convert SingleTextbox's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const singleTextboxRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { layout, validation, required, fieldSet } = q;
    return {
        ...respondentSurveyMapping(q),
        ...singleTextboxCrossCapabilityMapping(q),
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        fieldSet,
        // can only be required `all` of OpenEnded questions
        required: mapRespondentSurveyRequiredAll(required),
        validation: validation ?? undefined,
    };
};

// Mappings to convert SingleTextbox's external to it's internal QuestionType representation
// in the AggregateResponseDashboard Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const singleTextboxAggregateResponseDashboardMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...singleTextboxCrossCapabilityMapping(q),
        ...aggregateResponseDashboardMapping(q),
    };
};

// Mappings to convert SingleTextbox's external to it's internal QuestionType representation
// in the AggregateResponseCustomizableSolutions Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const singleTextboxAggregateResponseCustomizableSolutionsMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...singleTextboxCrossCapabilityMapping(q),
        ...aggregateResponseCustomizableSolutionsMapping(q),
    };
};

// Mappings/Types for Matrix shared by all Capabilities
/**
 * Returns fields for Matrix's internal QuestionType type
 * that are shared across all Capabilities
 */
const matrixCrossCapabilityMapping = ({ id, family, variant, }) => ({
    typeId: 'matrix',
    family: family,
    variant: variant,
    id,
});

// Mappings to convert Matrix's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
const isExternalMatrixWithRadio = (q) => q.variant === 'SINGLE_ANSWER_RADIO' && 'fieldSets' in q;
const isExternalMatrixWithCheckboxes = (q) => q.variant === 'MANY_ANSWERS_CHECKBOX';
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const matrixRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { required, answerOther, validation } = q;
    const base = {
        ...respondentSurveyMapping(q),
        ...matrixCrossCapabilityMapping(q),
        required: required ?? undefined,
        validation: validation ?? undefined,
        ...mapRespondentSurveyAnswerOther(answerOther),
    };
    if (isExternalMatrixWithRadio(q)) {
        const { fieldSets, answersWeighted, answerNA, isForcedRanking, isWeighted, layout, } = q;
        const newLayout = {
            ...layout,
            spacing: fillSpacing(layout.spacing),
        };
        return {
            ...base,
            fieldSets,
            answersWeighted,
            answerNotApplicable: answerNA ?? undefined,
            isForcedRanking,
            isWeighted,
            layout: newLayout,
        };
    }
    if (isExternalMatrixWithCheckboxes(q)) {
        const { fieldSets, answers, layout } = q;
        const newLayout = {
            ...layout,
            spacing: fillSpacing(layout.spacing),
        };
        return {
            ...base,
            fieldSets,
            answers,
            layout: newLayout,
        };
    }
    // default return
    const { fieldSet, answersWeighted, answerNA, layout, } = q;
    const newLayout = {
        ...layout,
        spacing: fillSpacing(layout.spacing),
    };
    return {
        ...base,
        layout: newLayout,
        fieldSet,
        answersWeighted,
        answerNotApplicable: answerNA ?? undefined,
    };
};

// Mappings to convert Matrix's external to it's internal QuestionType representation
// in the AggregateResponseDashboard Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const matrixAggregateResponseDashboardMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...matrixCrossCapabilityMapping(q),
        ...aggregateResponseDashboardMapping(q),
    };
};

// Mappings to convert Matrix's external to it's internal QuestionType representation
// in the AggregateResponseCustomizableSolutions Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const matrixAggregateResponseCustomizableSolutionsMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...matrixCrossCapabilityMapping(q),
        ...aggregateResponseCustomizableSolutionsMapping(q),
    };
};

// Mappings/Types for MultipleChoice shared by all Capabilities
/**
 * Returns fields for MultipleChoice's internal QuestionType type
 * that are shared across all Capabilities
 */
const multipleChoiceCrossCapabilityMapping = ({ id, }) => ({
    typeId: 'multiple_choice',
    family: 'MULTIPLE_CHOICE',
    variant: 'SINGLE_ANSWER_RADIO',
    id,
});

// Mappings to convert MultipleChoice's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const multipleChoiceRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { layout, validation, required, answers, answerOther, answerNA } = q;
    return {
        ...respondentSurveyMapping(q),
        ...multipleChoiceCrossCapabilityMapping(q),
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        required: mapRespondentSurveyRequiredAll(required),
        validation: validation ?? undefined,
        answers,
        answerNoneOfTheAbove: answerNA ?? undefined,
        ...mapRespondentSurveyAnswerOther(answerOther),
    };
};

// Mappings to convert MultipleChoice's external to it's internal QuestionType representation
// in the RespondentQuizResults Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const multipleChoiceRespondentQuizResultsMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { layout, answerOther, answerNA } = q;
    return {
        ...respondentQuizResultsMapping(q),
        ...multipleChoiceCrossCapabilityMapping(q),
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        answerOther: mapRespondentQuizResultsAnswerOther(answerOther),
        answerNoneOfTheAbove: answerNA ?? null, // remove `null` as possible type
    };
};

// Mappings to convert MultipleChoice's external to it's internal QuestionType representation
// in the AggregateResponseDashboard Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const multipleChoiceAggregateResponseDashboardMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...multipleChoiceCrossCapabilityMapping(q),
        ...aggregateResponseDashboardMapping(q),
    };
};

// Mappings to convert MultipleChoice's external to it's internal QuestionType representation
// in the AggregateResponseCustomizableSolutions Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const multipleChoiceAggregateResponseCustomizableSolutionsMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...multipleChoiceCrossCapabilityMapping(q),
        ...aggregateResponseCustomizableSolutionsMapping(q),
    };
};

// Mappings/Types for MultipleTextbox shared by all Capabilities
/**
 * Returns fields for MultipleTextbox's internal QuestionType type
 * that are shared across all Capabilities
 */
const multipleTextboxCrossCapabilityMapping = ({ id, }) => ({
    typeId: 'multiple_textbox',
    family: 'OPEN_ENDED',
    variant: 'MANY_FIELDS',
    id,
});

// Mappings to convert MultipleTextbox's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const multipleTextboxRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { layout, answers, validation, sumValidation, required, fieldSets } = q;
    return {
        ...respondentSurveyMapping(q),
        ...multipleTextboxCrossCapabilityMapping(q),
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        fieldSets,
        answers,
        required: required ?? undefined,
        validation: validation ?? undefined,
        sumValidation: sumValidation ?? undefined,
    };
};

// Mappings to convert MultipleTextbox's external to it's internal QuestionType representation
// in the AggregateResponseDashboard Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const multipleTextboxAggregateResponseDashboardMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...multipleTextboxCrossCapabilityMapping(q),
        ...aggregateResponseDashboardMapping(q),
    };
};

// Mappings/Types for Ranking shared by all Capabilities
/**
 * Returns fields for Ranking's internal QuestionType type
 * that are shared across all Capabilities
 */
const rankingCrossCapabilityMapping = ({ id, }) => ({
    typeId: 'ranking',
    family: 'RANKING',
    variant: 'SINGLE_ANSWER_RADIO',
    id,
});

// Mappings to convert Ranking's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const rankingRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { layout, fieldSets, answers, required, answerNA } = q;
    return {
        ...respondentSurveyMapping(q),
        ...rankingCrossCapabilityMapping(q),
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        fieldSets,
        answers,
        required: mapRespondentSurveyRequiredAll(required),
        answerNoneOfTheAbove: answerNA ?? undefined,
    };
};

// Mappings to convert Ranking's external to it's internal QuestionType representation
// in the AggregateResponseDashboard Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const rankingAggregateResponseDashboardMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...rankingCrossCapabilityMapping(q),
        ...aggregateResponseDashboardMapping(q),
    };
};

// Mappings to convert Ranking's external to it's internal QuestionType representation
// in the AggregateResponseCustomizableSolutions Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const rankingAggregateResponseCustomizableSolutionsMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...rankingCrossCapabilityMapping(q),
        ...aggregateResponseCustomizableSolutionsMapping(q),
    };
};

// Mappings/Types for Slider shared by all Capabilities
/**
 * Returns fields for Slider's internal QuestionType type
 * that are shared across all Capabilities
 */
const sliderCrossCapabilityMapping = ({ id, }) => ({
    typeId: 'slider',
    family: 'SLIDER',
    variant: 'SINGLE_SLIDER',
    id,
});

// Mappings to convert Slider's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const sliderRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { scaleOptions, required, hideNumericalInput } = q;
    return {
        ...respondentSurveyMapping(q),
        ...sliderCrossCapabilityMapping(q),
        required: required ?? undefined,
        scaleOptions,
        hideNumericalInput,
    };
};

// Mappings to convert Slider's external to it's internal QuestionType representation
// in the AggregateResponseDashboard Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const sliderAggregateResponseDashboardMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...sliderCrossCapabilityMapping(q),
        ...aggregateResponseDashboardMapping(q),
    };
};

// Mappings/Types for StarRating shared by all Capabilities
/**
 * Returns fields for StarRating's internal QuestionType type
 * that are shared across all Capabilities
 */
const starRatingCrossCapabilityMapping = ({ id, family, variant, }) => ({
    typeId: 'star_rating',
    family,
    variant,
    id,
});

// Mappings to convert StarRating's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const starRatingRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { layout, required, fieldSet, answersWeighted, answerNA, answerOther, symbolOptions, validation, } = q;
    return {
        ...respondentSurveyMapping(q),
        ...starRatingCrossCapabilityMapping(q),
        family: 'RATING_SCALE',
        variant: 'SINGLE_ANSWER_SYMBOL',
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        required: mapRespondentSurveyRequiredAll(required),
        validation: validation ?? undefined,
        fieldSet,
        answersWeighted,
        answerNoneOfTheAbove: answerNA ?? undefined,
        symbolOptions: symbolOptions ?? undefined,
        ...mapRespondentSurveyAnswerOther(answerOther),
    };
};

// Mappings to convert StarRating's external to it's internal QuestionType representation
// in the AggregateResponseDashboard Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const starRatingAggregateResponseDashboardMapping = (externalQuestion) => {
    const q = externalQuestion;
    return {
        ...starRatingCrossCapabilityMapping(q),
        ...aggregateResponseDashboardMapping(q),
    };
};

// Mappings/Types for ImagePresentation shared by all Capabilities
/**
 * Returns fields for ImagePresentation's internal QuestionType type
 * that are shared across all Capabilities
 */
const imagePresentationCrossCapabilityMapping = ({ id, }) => ({
    typeId: 'image_presentation',
    family: 'PRESENTATION',
    variant: 'IMAGE',
    id,
});

// Mappings to convert ImagePresentation's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const imagePresentationRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { heading, layout } = q;
    return {
        ...respondentSurveyMapping(q),
        ...imagePresentationCrossCapabilityMapping(q),
        heading: {
            ...heading,
            assignments: heading.assignments ?? undefined,
        },
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
    };
};

// Mappings/Types for TextPresentation shared by all Capabilities
/**
 * Returns fields for TextPresentation's internal QuestionType type
 * that are shared across all Capabilities
 */
const textPresentationCrossCapabilityMapping = ({ id, }) => ({
    typeId: 'text_presentation',
    family: 'PRESENTATION',
    variant: 'TEXT',
    id,
});

// Mappings to convert TextPresentation's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const textPresentationRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { heading, layout } = q;
    return {
        ...respondentSurveyMapping(q),
        ...textPresentationCrossCapabilityMapping(q),
        heading,
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
    };
};

// Mappings/Types for NPS shared by all Capabilities
/**
 * Returns fields for Nps's internal QuestionType type
 * that are shared across all Capabilities
 */
const npsCrossCapabilityMapping = ({ id, }) => ({
    typeId: 'nps',
    family: 'RATING_SCALE',
    variant: 'NPS',
    id,
});

// Mappings to convert NPS's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const npsRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { layout, required, answersWeighted, fieldSet } = q;
    return {
        ...respondentSurveyMapping(q),
        ...npsCrossCapabilityMapping(q),
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        required: mapRespondentSurveyRequiredAll(required) ||
            mapRespondentSurveyRequiredAtLeast(required),
        answersWeighted,
        fieldSet,
    };
};

const isTextLengthValidation = typeGuard({
    errorMessage: isString,
    type: isFixed('TEXT_LENGTH'),
    minLength: isNumber,
    maxLength: isNumber,
});
const isNumericalValidation = typeGuard({
    errorMessage: isString,
    type: isEither([isFixed('INTEGER'), isFixed('DECIMAL')]),
    minValue: isNumber,
    maxValue: isNumber,
});
const isDateValidation = typeGuard({
    errorMessage: isString,
    type: isOneOf(['DATE_US', 'DATE_INTL']),
    minDate: isString,
    maxDate: isString,
});
const isDateFormatValidation = typeGuard({
    errorMessage: isString,
    type: isOneOf(['DATE_US', 'DATE_INTL']),
});
const isEmailValidation = typeGuard({
    errorMessage: isString,
    type: isFixed('EMAIL'),
});
const isFieldValidation = isEither([
    isTextLengthValidation,
    isNumericalValidation,
    isDateValidation,
    isEmailValidation,
]);

// Mappings/Types for DateTime shared by all Capabilities
/**
 * Returns fields for DateTime's internal QuestionType type
 * that are shared across all Capabilities
 */
const dateTimeCrossCapabilityMapping = ({ id, variant, }) => ({
    typeId: 'date_time',
    family: 'DATETIME',
    variant: variant,
    id,
});

// Mappings to convert DateTime's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const dateTimeRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { layout, required, validation, fieldSets } = q;
    return {
        ...respondentSurveyMapping(q),
        ...dateTimeCrossCapabilityMapping(q),
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        required: required ?? undefined,
        validation: isDateFormatValidation(validation)
            ? { type: validation.type, errorMessage: validation.errorMessage }
            : { type: 'DATE_INTL', errorMessage: '' },
        fieldSets,
    };
};

// Mappings/Types for Matrix shared by all Capabilities
/**
 * Returns fields for Matrix's internal QuestionType type
 * that are shared across all Capabilities
 */
const matrixDropdownCrossCapabilityMapping = ({ id, family, variant, }) => ({
    typeId: 'matrix_dropdown',
    family: family,
    variant: variant,
    id,
});

// Mappings to convert Matrix's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const matrixDropdownRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { layout, required, answerOther, answersNested, fieldSets, validation, } = q;
    const base = {
        ...respondentSurveyMapping(q),
        ...matrixDropdownCrossCapabilityMapping(q),
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        required: required ?? undefined,
        validation: validation ?? undefined,
        ...mapRespondentSurveyAnswerOther(answerOther),
    };
    return {
        ...base,
        answersNested,
        fieldSets,
    };
};

// Mappings/Types for fileUpload shared by all Capabilities
/**
 * Returns fields for fileUpload's internal QuestionType type
 * that are shared across all Capabilities
 */
const fileUploadCrossCapabilityMapping = ({ id, variant, }) => ({
    typeId: 'file_upload',
    family: 'FILE_UPLOAD',
    variant: variant,
    id,
});

// Mappings to convert FileUpload's external to it's internal QuestionType representation
// in the RespondentSurvey Capability
/**
 * Maps the external QuestionType (GQL based) to the internal QuestionType
 *
 * _Types are mostly the same but the internal type is optimized to be easier to work with_
 */
const fileUploadRespondentSurveyMapping = (externalQuestion) => {
    const q = externalQuestion;
    const { required, layout, validation, instructions } = q;
    return {
        ...respondentSurveyMapping(q),
        ...fileUploadCrossCapabilityMapping(q),
        layout: { ...layout, spacing: fillSpacing(layout.spacing) },
        required: mapRespondentSurveyRequiredAll(required),
        instructions: instructions ?? undefined,
        validation: {
            errorMessage: validation?.errorMessage ?? '',
            formats: validation?.formats ?? [],
            type: validation?.type ?? 'FILE_FORMAT',
        },
    };
};

/** List of all SMQuestions supported capabilities (others might get defined via custom providers/views) */
const CAPABILITIES = [
    'respondent-survey',
    'respondent-quiz-results',
    'aggregate-response-dashboard',
    'aggregate-response-customizable-solutions',
];

const invalidCombination = () => {
    throw new Error('Invalid question and capability combination or not yet implemented');
};
/**
 * Main mapping to resolve QuestionType and Capability to
 * a mapping (and in it the assertion)
 */
const questionTypeAndCapabilityMapping = {
    single_textbox: {
        'respondent-survey': singleTextboxRespondentSurveyMapping,
        'respondent-quiz-results': invalidCombination,
        'aggregate-response-dashboard': singleTextboxAggregateResponseDashboardMapping,
        'aggregate-response-customizable-solutions': singleTextboxAggregateResponseCustomizableSolutionsMapping,
    },
    comment_box: {
        'respondent-survey': commentBoxRespondentSurveyMapping,
        'respondent-quiz-results': invalidCombination,
        'aggregate-response-dashboard': commentBoxAggregateResponseDashboardMapping,
        'aggregate-response-customizable-solutions': commentBoxAggregateResponseCustomizableSolutionsMapping,
    },
    multiple_textbox: {
        'respondent-survey': multipleTextboxRespondentSurveyMapping,
        'respondent-quiz-results': invalidCombination,
        'aggregate-response-dashboard': multipleTextboxAggregateResponseDashboardMapping,
        'aggregate-response-customizable-solutions': invalidCombination,
    },
    check_box: {
        'respondent-survey': checkBoxRespondentSurveyMapping,
        'respondent-quiz-results': checkBoxRespondentQuizResultsMapping,
        'aggregate-response-dashboard': checkBoxAggregateResponseDashboardMapping,
        'aggregate-response-customizable-solutions': checkBoxAggregateResponseCustomizableSolutionsMapping,
    },
    image_choice: {
        'respondent-survey': imageChoiceRespondentSurveyMapping,
        'respondent-quiz-results': imageChoiceRespondentQuizResultsMapping,
        'aggregate-response-dashboard': imageChoiceAggregateResponseDashboardMapping,
        'aggregate-response-customizable-solutions': imageChoiceAggregateResponseCustomizableSolutionsMapping,
    },
    multiple_choice: {
        'respondent-survey': multipleChoiceRespondentSurveyMapping,
        'respondent-quiz-results': multipleChoiceRespondentQuizResultsMapping,
        'aggregate-response-dashboard': multipleChoiceAggregateResponseDashboardMapping,
        'aggregate-response-customizable-solutions': multipleChoiceAggregateResponseCustomizableSolutionsMapping,
    },
    dropdown: {
        'respondent-survey': dropdownRespondentSurveyMapping,
        'respondent-quiz-results': dropdownRespondentQuizResultsMapping,
        'aggregate-response-dashboard': dropdownAggregateResponseDashboardMapping,
        'aggregate-response-customizable-solutions': dropdownAggregateResponseCustomizableSolutionsMapping,
    },
    slider: {
        'respondent-survey': sliderRespondentSurveyMapping,
        'respondent-quiz-results': invalidCombination,
        'aggregate-response-dashboard': sliderAggregateResponseDashboardMapping,
        'aggregate-response-customizable-solutions': invalidCombination,
    },
    star_rating: {
        'respondent-survey': starRatingRespondentSurveyMapping,
        'respondent-quiz-results': invalidCombination,
        'aggregate-response-dashboard': starRatingAggregateResponseDashboardMapping,
        'aggregate-response-customizable-solutions': invalidCombination,
    },
    ranking: {
        'respondent-survey': rankingRespondentSurveyMapping,
        'respondent-quiz-results': invalidCombination,
        'aggregate-response-dashboard': rankingAggregateResponseDashboardMapping,
        'aggregate-response-customizable-solutions': rankingAggregateResponseCustomizableSolutionsMapping,
    },
    matrix: {
        'respondent-survey': matrixRespondentSurveyMapping,
        'respondent-quiz-results': invalidCombination,
        'aggregate-response-dashboard': matrixAggregateResponseDashboardMapping,
        'aggregate-response-customizable-solutions': matrixAggregateResponseCustomizableSolutionsMapping,
    },
    matrix_dropdown: {
        'respondent-survey': matrixDropdownRespondentSurveyMapping,
        'respondent-quiz-results': invalidCombination,
        'aggregate-response-dashboard': invalidCombination,
        'aggregate-response-customizable-solutions': invalidCombination,
    },
    nps: {
        'respondent-survey': npsRespondentSurveyMapping,
        'respondent-quiz-results': invalidCombination,
        'aggregate-response-dashboard': invalidCombination,
        'aggregate-response-customizable-solutions': invalidCombination,
    },
    file_upload: {
        'respondent-survey': fileUploadRespondentSurveyMapping,
        'respondent-quiz-results': invalidCombination,
        'aggregate-response-dashboard': invalidCombination,
        'aggregate-response-customizable-solutions': invalidCombination,
    },
    date_time: {
        'respondent-survey': dateTimeRespondentSurveyMapping,
        'respondent-quiz-results': invalidCombination,
        'aggregate-response-dashboard': invalidCombination,
        'aggregate-response-customizable-solutions': invalidCombination,
    },
    image_presentation: {
        'respondent-survey': imagePresentationRespondentSurveyMapping,
        'respondent-quiz-results': invalidCombination,
        'aggregate-response-dashboard': invalidCombination,
        'aggregate-response-customizable-solutions': invalidCombination,
    },
    text_presentation: {
        'respondent-survey': textPresentationRespondentSurveyMapping,
        'respondent-quiz-results': invalidCombination,
        'aggregate-response-dashboard': invalidCombination,
        'aggregate-response-customizable-solutions': invalidCombination,
    },
};
/** type guard to check if string is a capability */
const isCapability = (u) => CAPABILITIES.includes(u);
/** Used to dynamically map the fields base on
 * * typeId
 * * question type
 * * capability
 * * combination of question type and capability
 */
const mapDynamic = ({ typeId, capability, externalQuestion, }) => {
    if (!isCapability(capability)) {
        // if this is a custom (unknown) capability just return the `externalQuestion` for now
        return externalQuestion;
    }
    return questionTypeAndCapabilityMapping[typeId][capability](externalQuestion);
};
const mapInternalDynamicToExternal = (externalQuestion, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
q, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
capability // keep for future use
) => {
    // possibly mirror `mapDynamic`s setup if needed later
    // remove internal `typeId` and other possible attributes from exported type
    const { 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    typeId, ...cleanExternalQuestion } = externalQuestion;
    return {
        ...cleanExternalQuestion,
    };
};

/**
 * Detects if a question belongs to this question type -
 * minimalistically as this function is supposed to be as optimized as possible as it will run multiple times
 */
const isCompatible$h = (q, typeId) => 'typeId' in q && q.typeId === typeId;

const baseGuards$d = baseQuestionGuards('single_textbox', 'OPEN_ENDED', 'SINGLE_LINE');
const isQuestionTypeSingleTextbox = typeGuard(baseGuards$d);
/**
 * Basic guard to determine question type
 *
 * throws an error when the question does not meet all the type requirements.
 */
function assertQuestionTypeSingleTextbox(q) {
    return typeAssertion(baseGuards$d)(q);
}

const mapFromExternalQuestion$g = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'single_textbox',
        externalQuestion,
        capability,
    });
    return questionType;
};

const typeId$g = 'single_textbox';
const fromExternalQuestion$g = (source, capability) => mapFromExternalQuestion$g(source, capability);
const toExternalQuestion$g = (eq, q, capability) => mapInternalDynamicToExternal(eq);
const isCompatible$g = (q) => isCompatible$h(q, typeId$g);
const isOfQuestionType$g = isQuestionTypeSingleTextbox;

/**
 * Helper to run validators and prepare their result
 *
 * @returns {QuestionError[] | null} `null` means there is no error - `QuestionError[]` will never be an empty array
 */
const runValidators = (
/** Map of validators */
validators, question, value, touched, validateEmptyValue = false) => {
    const validationErrors = validators.reduce((errors, validator) => {
        const newError = validator(question, value, touched, validateEmptyValue);
        return newError !== null ? [...errors, newError] : errors;
    }, []);
    if (validationErrors.length === 0) {
        return null;
    }
    return validationErrors;
};

function createValidationError(type, detail = '', questionId = null, fieldId = undefined) {
    return {
        type,
        detail,
        questionId,
        fieldId,
    };
}

/** returns `null` if valid and error object if not */
const validateTextLength = (
/** GraphQL `TextLengthValidation` entity  */
validation, 
/** Id to identify the question */
questionId, value, touched = false, 
/** Validate empty values (default `false`) */
validateEmptyValue = false) => {
    if ((!value && !validateEmptyValue) ||
        !validation ||
        !touched ||
        validation.type !== 'TEXT_LENGTH') {
        return null;
    }
    const { minLength, maxLength, errorMessage } = validation;
    if (value.length > maxLength || value.length < minLength) {
        return createValidationError(validation.type, errorMessage, questionId);
    }
    return null;
};
/** Validates `valueToValidate` for text-length and returns an `QuestionError` or `null` (valid) */
const checkValueTextLength = (question, valueToValidate, touched, validateEmptyValue = false) => {
    if (question.validation === undefined ||
        (!valueToValidate && !validateEmptyValue) ||
        question.validation?.type !== 'TEXT_LENGTH') {
        return null; // questionType does not support validation
    }
    const valueAsArray = Array.isArray(valueToValidate)
        ? valueToValidate
        : [valueToValidate];
    const errors = valueAsArray
        .map((value) => {
        return validateTextLength(question.validation, question.id, value || '', touched, validateEmptyValue);
    })
        .filter(notNull);
    return errors.length > 0 ? errors[0] : null;
};

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var isISO8601Exports = {};
var isISO8601$1 = {
  get exports(){ return isISO8601Exports; },
  set exports(v){ isISO8601Exports = v; },
};

var assertStringExports = {};
var assertString = {
  get exports(){ return assertStringExports; },
  set exports(v){ assertStringExports = v; },
};

(function (module, exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports.default = assertString;

	function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

	function assertString(input) {
	  var isString = typeof input === 'string' || input instanceof String;

	  if (!isString) {
	    var invalidType = _typeof(input);

	    if (input === null) invalidType = 'null';else if (invalidType === 'object') invalidType = input.constructor.name;
	    throw new TypeError("Expected a string but received a ".concat(invalidType));
	  }
	}

	module.exports = exports.default;
	module.exports.default = exports.default;
} (assertString, assertStringExports));

(function (module, exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports.default = isISO8601;

	var _assertString = _interopRequireDefault(assertStringExports);

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

	/* eslint-disable max-len */
	// from http://goo.gl/0ejHHW
	var iso8601 = /^([\+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-3])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T\s]((([01]\d|2[0-3])((:?)[0-5]\d)?|24:?00)([\.,]\d+(?!:))?)?(\17[0-5]\d([\.,]\d+)?)?([zZ]|([\+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/; // same as above, except with a strict 'T' separator between date and time

	var iso8601StrictSeparator = /^([\+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-3])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T]((([01]\d|2[0-3])((:?)[0-5]\d)?|24:?00)([\.,]\d+(?!:))?)?(\17[0-5]\d([\.,]\d+)?)?([zZ]|([\+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/;
	/* eslint-enable max-len */

	var isValidDate = function isValidDate(str) {
	  // str must have passed the ISO8601 check
	  // this check is meant to catch invalid dates
	  // like 2009-02-31
	  // first check for ordinal dates
	  var ordinalMatch = str.match(/^(\d{4})-?(\d{3})([ T]{1}\.*|$)/);

	  if (ordinalMatch) {
	    var oYear = Number(ordinalMatch[1]);
	    var oDay = Number(ordinalMatch[2]); // if is leap year

	    if (oYear % 4 === 0 && oYear % 100 !== 0 || oYear % 400 === 0) return oDay <= 366;
	    return oDay <= 365;
	  }

	  var match = str.match(/(\d{4})-?(\d{0,2})-?(\d*)/).map(Number);
	  var year = match[1];
	  var month = match[2];
	  var day = match[3];
	  var monthString = month ? "0".concat(month).slice(-2) : month;
	  var dayString = day ? "0".concat(day).slice(-2) : day; // create a date object and compare

	  var d = new Date("".concat(year, "-").concat(monthString || '01', "-").concat(dayString || '01'));

	  if (month && day) {
	    return d.getUTCFullYear() === year && d.getUTCMonth() + 1 === month && d.getUTCDate() === day;
	  }

	  return true;
	};

	function isISO8601(str) {
	  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
	  (0, _assertString.default)(str);
	  var check = options.strictSeparator ? iso8601StrictSeparator.test(str) : iso8601.test(str);
	  if (check && options.strict) return isValidDate(str);
	  return check;
	}

	module.exports = exports.default;
	module.exports.default = exports.default;
} (isISO8601$1, isISO8601Exports));

var isISO8601 = /*@__PURE__*/getDefaultExportFromCjs(isISO8601Exports);

const DATE_PATTERNS = {
    DATE_INTL: /^(?<dd>0?[1-9]|[12][0-9]|3[0-1])(\/{1})(?<mm>0?[1-9]|1[0-2])\2(?<yyyy>\d{4})$/,
    DATE_US: /^(?<mm>0?[1-9]|1[0-2])\/(?<dd>0?[1-9]|[12][0-9]|3[0-1])\/(?<yyyy>\d{4})$/,
};

const MIN_YEAR = 1900;
const MAX_YEAR = 9999;
const isDateValidationType = (validationType) => validationType === 'DATE_INTL' || validationType === 'DATE_US';
const stringToDate = (format) => (s) => {
    try {
        const { groups: { dd, mm, yyyy }, } = DATE_PATTERNS[format].exec(s);
        const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
        date.setUTCHours(0, 0, 0, 0);
        return date;
    }
    catch (e) {
        return null;
    }
};
const getDateYear = (format) => (s) => {
    try {
        const { groups: { yyyy }, } = DATE_PATTERNS[format].exec(s);
        return parseInt(yyyy, 10);
    }
    catch (e) {
        return 0;
    }
};
const isYearValid = (question, date) => {
    const getDateYearType = getDateYear(question.validation?.type);
    const year = getDateYearType(date);
    return year >= MIN_YEAR && year <= MAX_YEAR;
};
/**
 * Converts minDate and maxDate format: ISO Date-Time => Date
 * only when `validation` is `DateValidation`
 */
const getMinAndMaxDate = (validation) => {
    if (!isDateValidationType(validation.type) ||
        !('minDate' in validation && 'maxDate' in validation) ||
        !isISO8601(validation.minDate) ||
        !isISO8601(validation.maxDate)) {
        return [undefined, undefined]; // no min/max for DateFormatValidation
    }
    return [validation.minDate, validation.maxDate].map((isoDate) => {
        const date = new Date(isoDate);
        date.setUTCHours(0, 0, 0, 0);
        return date;
    });
};
/** returns `null` if valid and error object if not */
const validateDate = (
/** GraphQL `DateValidation` entity  */
validation, 
/** Id to identify the question */
questionId, value, touched = false, 
/** Validate empty values (default `false`) */
validateEmptyValue = false) => {
    if ((!value && !validateEmptyValue) ||
        !validation ||
        !touched ||
        !isDateValidationType(validation.type)) {
        return null;
    }
    const { errorMessage } = validation;
    const dateType = validation?.type;
    const stringToDateType = stringToDate(dateType);
    // value format: Short Date => Date
    const date = stringToDateType(value);
    // minDate and maxDate format: ISO Date-Time => Date
    const [dmin, dmax] = getMinAndMaxDate(validation);
    if (!date ||
        (dmax && date.getTime() > dmax.getTime()) ||
        (dmin && date.getTime() < dmin.getTime())) {
        return createValidationError(validation.type, errorMessage, questionId);
    }
    return null;
};
/** Validates `valueToValidate` as date and returns an `QuestionError` or `null` (valid) */
const checkValueDateFormat = (question, valueToValidate, touched, validateEmptyValue = false) => {
    if ((!valueToValidate && !validateEmptyValue) ||
        !isDateValidationType(question?.validation?.type)) {
        return null; // questionType does not support validation
    }
    const valueAsArray = Array.isArray(valueToValidate)
        ? valueToValidate
        : [valueToValidate];
    const errors = valueAsArray
        .map((value) => {
        return validateDate(question.validation, question.id, value || '', touched, validateEmptyValue);
    })
        .filter(notNull);
    return errors.length > 0 ? errors[0] : null;
};

const isNumericalValidationType = (validationType) => validationType === 'INTEGER' || validationType === 'DECIMAL';
const isSumValidationType = (validationType) => validationType === 'SUM';
/** Integers can only contain numbers (`0-9`) */
const isValidInteger = (maybeInteger = '') => maybeInteger.match(/^[0-9]+$/) !== null;
const isValidDecimal = (value = '') => value.match(/^[+-]?([0-9]+\.?[0-9]*|\.[0-9]+)$/) !== null;
/** returns `null` if valid and error object if not */
const validateNumber = (
/** GraphQL `DateValidation` entity  */
validation, 
/** Id to identify the question */
questionId, value, touched = false, 
/** Validate empty values (default `false`) */
validateEmptyValue = false) => {
    if ((!value && !validateEmptyValue) ||
        !validation ||
        !touched ||
        !isNumericalValidationType(validation.type)) {
        return null;
    }
    const { minValue, maxValue, errorMessage } = validation;
    const parsedNumber = parseFloat(value);
    if (Number.isNaN(parsedNumber) ||
        (validation.type === 'INTEGER' && !isValidInteger(value)) ||
        (validation.type === 'DECIMAL' && !isValidDecimal(value)) ||
        Boolean(maxValue && parsedNumber > maxValue) ||
        Boolean(minValue && parsedNumber < minValue)) {
        return createValidationError(validation.type, errorMessage, questionId);
    }
    return null;
};
/** returns `null` if valid and error object if not */
const validateFixedSum = (
/** GraphQL `SumValidation` entity  */
validation, 
/** Id to identify the question */
questionId, values, touched = false, 
/** Validate empty values (default `false`) */
validateEmptyValue = false) => {
    if ((!values && !validateEmptyValue) ||
        !validation ||
        !touched ||
        !isSumValidationType(validation.type)) {
        return null;
    }
    const { sum, errorMessage } = validation;
    const valueSum = values.reduce((reduceSum, value) => {
        const parsedNumber = parseFloat(value);
        return reduceSum + parsedNumber;
    }, 0);
    if ((valueSum && valueSum !== sum) ||
        Number.isNaN(valueSum) ||
        !isValidInteger(String(valueSum))) {
        return createValidationError(validation.type, errorMessage, questionId);
    }
    return null;
};
/** Validates `valueToValidate` as number and returns an `QuestionError` or `null` (valid) */
const checkValueNumericalFormat = (question, valueToValidate = '', touched = false, validateEmptyValue = false) => {
    if ((!valueToValidate && !validateEmptyValue) ||
        !isNumericalValidationType(question?.validation?.type)) {
        return null; // questionType does not support validation
    }
    const valueAsArray = Array.isArray(valueToValidate)
        ? valueToValidate
        : [valueToValidate];
    const errors = valueAsArray
        .map((value) => {
        return validateNumber(question.validation, question.id, value || '', touched, validateEmptyValue);
    })
        .filter(notNull);
    return errors.length > 0 ? errors[0] : null;
};
const checkMultipleValueNumericalSum = (question, valueToValidate = [], touched = false, validateEmptyValue = false) => {
    if ((!valueToValidate && !validateEmptyValue) ||
        !question.sumValidation ||
        !isSumValidationType(question?.sumValidation?.type)) {
        return null; // questionType does not support validation
    }
    return validateFixedSum(question.sumValidation, question.id, valueToValidate, touched, validateEmptyValue);
};

var isEmailExports = {};
var isEmail$1 = {
  get exports(){ return isEmailExports; },
  set exports(v){ isEmailExports = v; },
};

var mergeExports = {};
var merge = {
  get exports(){ return mergeExports; },
  set exports(v){ mergeExports = v; },
};

(function (module, exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports.default = merge;

	function merge() {
	  var obj = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
	  var defaults = arguments.length > 1 ? arguments[1] : undefined;

	  for (var key in defaults) {
	    if (typeof obj[key] === 'undefined') {
	      obj[key] = defaults[key];
	    }
	  }

	  return obj;
	}

	module.exports = exports.default;
	module.exports.default = exports.default;
} (merge, mergeExports));

var isByteLengthExports = {};
var isByteLength = {
  get exports(){ return isByteLengthExports; },
  set exports(v){ isByteLengthExports = v; },
};

(function (module, exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports.default = isByteLength;

	var _assertString = _interopRequireDefault(assertStringExports);

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

	function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

	/* eslint-disable prefer-rest-params */
	function isByteLength(str, options) {
	  (0, _assertString.default)(str);
	  var min;
	  var max;

	  if (_typeof(options) === 'object') {
	    min = options.min || 0;
	    max = options.max;
	  } else {
	    // backwards compatibility: isByteLength(str, min [, max])
	    min = arguments[1];
	    max = arguments[2];
	  }

	  var len = encodeURI(str).split(/%..|./).length - 1;
	  return len >= min && (typeof max === 'undefined' || len <= max);
	}

	module.exports = exports.default;
	module.exports.default = exports.default;
} (isByteLength, isByteLengthExports));

var isFQDNExports = {};
var isFQDN = {
  get exports(){ return isFQDNExports; },
  set exports(v){ isFQDNExports = v; },
};

(function (module, exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports.default = isFQDN;

	var _assertString = _interopRequireDefault(assertStringExports);

	var _merge = _interopRequireDefault(mergeExports);

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

	var default_fqdn_options = {
	  require_tld: true,
	  allow_underscores: false,
	  allow_trailing_dot: false,
	  allow_numeric_tld: false
	};

	function isFQDN(str, options) {
	  (0, _assertString.default)(str);
	  options = (0, _merge.default)(options, default_fqdn_options);
	  /* Remove the optional trailing dot before checking validity */

	  if (options.allow_trailing_dot && str[str.length - 1] === '.') {
	    str = str.substring(0, str.length - 1);
	  }

	  var parts = str.split('.');
	  var tld = parts[parts.length - 1];

	  if (options.require_tld) {
	    // disallow fqdns without tld
	    if (parts.length < 2) {
	      return false;
	    }

	    if (!/^([a-z\u00a1-\uffff]{2,}|xn[a-z0-9-]{2,})$/i.test(tld)) {
	      return false;
	    } // disallow spaces && special characers


	    if (/[\s\u2002-\u200B\u202F\u205F\u3000\uFEFF\uDB40\uDC20\u00A9\uFFFD]/.test(tld)) {
	      return false;
	    }
	  } // reject numeric TLDs


	  if (!options.allow_numeric_tld && /^\d+$/.test(tld)) {
	    return false;
	  }

	  return parts.every(function (part) {
	    if (part.length > 63) {
	      return false;
	    }

	    if (!/^[a-z_\u00a1-\uffff0-9-]+$/i.test(part)) {
	      return false;
	    } // disallow full-width chars


	    if (/[\uff01-\uff5e]/.test(part)) {
	      return false;
	    } // disallow parts starting or ending with hyphen


	    if (/^-|-$/.test(part)) {
	      return false;
	    }

	    if (!options.allow_underscores && /_/.test(part)) {
	      return false;
	    }

	    return true;
	  });
	}

	module.exports = exports.default;
	module.exports.default = exports.default;
} (isFQDN, isFQDNExports));

var isIPExports = {};
var isIP = {
  get exports(){ return isIPExports; },
  set exports(v){ isIPExports = v; },
};

(function (module, exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports.default = isIP;

	var _assertString = _interopRequireDefault(assertStringExports);

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

	/**
	11.3.  Examples

	   The following addresses

	             fe80::1234 (on the 1st link of the node)
	             ff02::5678 (on the 5th link of the node)
	             ff08::9abc (on the 10th organization of the node)

	   would be represented as follows:

	             fe80::1234%1
	             ff02::5678%5
	             ff08::9abc%10

	   (Here we assume a natural translation from a zone index to the
	   <zone_id> part, where the Nth zone of any scope is translated into
	   "N".)

	   If we use interface names as <zone_id>, those addresses could also be
	   represented as follows:

	            fe80::1234%ne0
	            ff02::5678%pvc1.3
	            ff08::9abc%interface10

	   where the interface "ne0" belongs to the 1st link, "pvc1.3" belongs
	   to the 5th link, and "interface10" belongs to the 10th organization.
	 * * */
	var ipv4Maybe = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;
	var ipv6Block = /^[0-9A-F]{1,4}$/i;

	function isIP(str) {
	  var version = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
	  (0, _assertString.default)(str);
	  version = String(version);

	  if (!version) {
	    return isIP(str, 4) || isIP(str, 6);
	  } else if (version === '4') {
	    if (!ipv4Maybe.test(str)) {
	      return false;
	    }

	    var parts = str.split('.').sort(function (a, b) {
	      return a - b;
	    });
	    return parts[3] <= 255;
	  } else if (version === '6') {
	    var addressAndZone = [str]; // ipv6 addresses could have scoped architecture
	    // according to https://tools.ietf.org/html/rfc4007#section-11

	    if (str.includes('%')) {
	      addressAndZone = str.split('%');

	      if (addressAndZone.length !== 2) {
	        // it must be just two parts
	        return false;
	      }

	      if (!addressAndZone[0].includes(':')) {
	        // the first part must be the address
	        return false;
	      }

	      if (addressAndZone[1] === '') {
	        // the second part must not be empty
	        return false;
	      }
	    }

	    var blocks = addressAndZone[0].split(':');
	    var foundOmissionBlock = false; // marker to indicate ::
	    // At least some OS accept the last 32 bits of an IPv6 address
	    // (i.e. 2 of the blocks) in IPv4 notation, and RFC 3493 says
	    // that '::ffff:a.b.c.d' is valid for IPv4-mapped IPv6 addresses,
	    // and '::a.b.c.d' is deprecated, but also valid.

	    var foundIPv4TransitionBlock = isIP(blocks[blocks.length - 1], 4);
	    var expectedNumberOfBlocks = foundIPv4TransitionBlock ? 7 : 8;

	    if (blocks.length > expectedNumberOfBlocks) {
	      return false;
	    } // initial or final ::


	    if (str === '::') {
	      return true;
	    } else if (str.substr(0, 2) === '::') {
	      blocks.shift();
	      blocks.shift();
	      foundOmissionBlock = true;
	    } else if (str.substr(str.length - 2) === '::') {
	      blocks.pop();
	      blocks.pop();
	      foundOmissionBlock = true;
	    }

	    for (var i = 0; i < blocks.length; ++i) {
	      // test for a :: which can not be at the string start/end
	      // since those cases have been handled above
	      if (blocks[i] === '' && i > 0 && i < blocks.length - 1) {
	        if (foundOmissionBlock) {
	          return false; // multiple :: in address
	        }

	        foundOmissionBlock = true;
	      } else if (foundIPv4TransitionBlock && i === blocks.length - 1) ; else if (!ipv6Block.test(blocks[i])) {
	        return false;
	      }
	    }

	    if (foundOmissionBlock) {
	      return blocks.length >= 1;
	    }

	    return blocks.length === expectedNumberOfBlocks;
	  }

	  return false;
	}

	module.exports = exports.default;
	module.exports.default = exports.default;
} (isIP, isIPExports));

(function (module, exports) {

	Object.defineProperty(exports, "__esModule", {
	  value: true
	});
	exports.default = isEmail;

	var _assertString = _interopRequireDefault(assertStringExports);

	var _merge = _interopRequireDefault(mergeExports);

	var _isByteLength = _interopRequireDefault(isByteLengthExports);

	var _isFQDN = _interopRequireDefault(isFQDNExports);

	var _isIP = _interopRequireDefault(isIPExports);

	function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

	function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

	function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

	function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

	function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

	function _iterableToArrayLimit(arr, i) { if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return; var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

	function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

	var default_email_options = {
	  allow_display_name: false,
	  require_display_name: false,
	  allow_utf8_local_part: true,
	  require_tld: true,
	  blacklisted_chars: '',
	  ignore_max_length: false
	};
	/* eslint-disable max-len */

	/* eslint-disable no-control-regex */

	var splitNameAddress = /^([^\x00-\x1F\x7F-\x9F\cX]+)<(.+)>$/i;
	var emailUserPart = /^[a-z\d!#\$%&'\*\+\-\/=\?\^_`{\|}~]+$/i;
	var gmailUserPart = /^[a-z\d]+$/;
	var quotedEmailUser = /^([\s\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e]|(\\[\x01-\x09\x0b\x0c\x0d-\x7f]))*$/i;
	var emailUserUtf8Part = /^[a-z\d!#\$%&'\*\+\-\/=\?\^_`{\|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+$/i;
	var quotedEmailUserUtf8 = /^([\s\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|(\\[\x01-\x09\x0b\x0c\x0d-\x7f\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))*$/i;
	var defaultMaxEmailLength = 254;
	/* eslint-enable max-len */

	/* eslint-enable no-control-regex */

	/**
	 * Validate display name according to the RFC2822: https://tools.ietf.org/html/rfc2822#appendix-A.1.2
	 * @param {String} display_name
	 */

	function validateDisplayName(display_name) {
	  var trim_quotes = display_name.match(/^"(.+)"$/i);
	  var display_name_without_quotes = trim_quotes ? trim_quotes[1] : display_name; // display name with only spaces is not valid

	  if (!display_name_without_quotes.trim()) {
	    return false;
	  } // check whether display name contains illegal character


	  var contains_illegal = /[\.";<>]/.test(display_name_without_quotes);

	  if (contains_illegal) {
	    // if contains illegal characters,
	    // must to be enclosed in double-quotes, otherwise it's not a valid display name
	    if (!trim_quotes) {
	      return false;
	    } // the quotes in display name must start with character symbol \


	    var all_start_with_back_slash = display_name_without_quotes.split('"').length === display_name_without_quotes.split('\\"').length;

	    if (!all_start_with_back_slash) {
	      return false;
	    }
	  }

	  return true;
	}

	function isEmail(str, options) {
	  (0, _assertString.default)(str);
	  options = (0, _merge.default)(options, default_email_options);

	  if (options.require_display_name || options.allow_display_name) {
	    var display_email = str.match(splitNameAddress);

	    if (display_email) {
	      var display_name;

	      var _display_email = _slicedToArray(display_email, 3);

	      display_name = _display_email[1];
	      str = _display_email[2];

	      // sometimes need to trim the last space to get the display name
	      // because there may be a space between display name and email address
	      // eg. myname <address@gmail.com>
	      // the display name is `myname` instead of `myname `, so need to trim the last space
	      if (display_name.endsWith(' ')) {
	        display_name = display_name.substr(0, display_name.length - 1);
	      }

	      if (!validateDisplayName(display_name)) {
	        return false;
	      }
	    } else if (options.require_display_name) {
	      return false;
	    }
	  }

	  if (!options.ignore_max_length && str.length > defaultMaxEmailLength) {
	    return false;
	  }

	  var parts = str.split('@');
	  var domain = parts.pop();
	  var user = parts.join('@');
	  var lower_domain = domain.toLowerCase();

	  if (options.domain_specific_validation && (lower_domain === 'gmail.com' || lower_domain === 'googlemail.com')) {
	    /*
	      Previously we removed dots for gmail addresses before validating.
	      This was removed because it allows `multiple..dots@gmail.com`
	      to be reported as valid, but it is not.
	      Gmail only normalizes single dots, removing them from here is pointless,
	      should be done in normalizeEmail
	    */
	    user = user.toLowerCase(); // Removing sub-address from username before gmail validation

	    var username = user.split('+')[0]; // Dots are not included in gmail length restriction

	    if (!(0, _isByteLength.default)(username.replace('.', ''), {
	      min: 6,
	      max: 30
	    })) {
	      return false;
	    }

	    var _user_parts = username.split('.');

	    for (var i = 0; i < _user_parts.length; i++) {
	      if (!gmailUserPart.test(_user_parts[i])) {
	        return false;
	      }
	    }
	  }

	  if (options.ignore_max_length === false && (!(0, _isByteLength.default)(user, {
	    max: 64
	  }) || !(0, _isByteLength.default)(domain, {
	    max: 254
	  }))) {
	    return false;
	  }

	  if (!(0, _isFQDN.default)(domain, {
	    require_tld: options.require_tld
	  })) {
	    if (!options.allow_ip_domain) {
	      return false;
	    }

	    if (!(0, _isIP.default)(domain)) {
	      if (!domain.startsWith('[') || !domain.endsWith(']')) {
	        return false;
	      }

	      var noBracketdomain = domain.substr(1, domain.length - 2);

	      if (noBracketdomain.length === 0 || !(0, _isIP.default)(noBracketdomain)) {
	        return false;
	      }
	    }
	  }

	  if (user[0] === '"') {
	    user = user.slice(1, user.length - 1);
	    return options.allow_utf8_local_part ? quotedEmailUserUtf8.test(user) : quotedEmailUser.test(user);
	  }

	  var pattern = options.allow_utf8_local_part ? emailUserUtf8Part : emailUserPart;
	  var user_parts = user.split('.');

	  for (var _i2 = 0; _i2 < user_parts.length; _i2++) {
	    if (!pattern.test(user_parts[_i2])) {
	      return false;
	    }
	  }

	  if (options.blacklisted_chars) {
	    if (user.search(new RegExp("[".concat(options.blacklisted_chars, "]+"), 'g')) !== -1) return false;
	  }

	  return true;
	}

	module.exports = exports.default;
	module.exports.default = exports.default;
} (isEmail$1, isEmailExports));

var isEmail = /*@__PURE__*/getDefaultExportFromCjs(isEmailExports);

/** returns `null` if valid and error object if not */
const validateEmail = (validation, 
/** Id to identify the question */
questionId, value, touched = false, 
/** Validate empty values (default `false`) */
validateEmptyValue = false) => {
    if ((!value && !validateEmptyValue) ||
        !validation ||
        !touched ||
        validation?.type !== 'EMAIL') {
        return null;
    }
    const { errorMessage } = validation;
    if (!isEmail(value)) {
        return createValidationError(validation.type, errorMessage, questionId);
    }
    return null;
};
/** Validates `valueToValidate` as email and returns an `QuestionError` or `null` (valid) */
const checkValueEmailFormat = (question, valueToValidate = '', touched = false, validateEmptyValue = false) => {
    if (question.validation === undefined ||
        (!valueToValidate && !validateEmptyValue) ||
        question.validation?.type !== 'EMAIL') {
        return null; // questionType does not support validation
    }
    const valueAsArray = Array.isArray(valueToValidate)
        ? valueToValidate
        : [valueToValidate];
    const errors = valueAsArray
        .map((value) => {
        return validateEmail(question.validation, question.id, value || '', touched, validateEmptyValue);
    })
        .filter(notNull);
    return errors.length > 0 ? errors[0] : null;
};

const isResponseValueArray = (value) => {
    return (Array.isArray(value) &&
        !value.some((v) => typeof v !== 'object' || !('id' in v) || !('value' in v)) // no invalid element in array
    );
};
/** If there is a selected other type `ANSWER` input make sure it is not empty. */
const hasEmptyOtherAnswer = (value) => {
    return value.some((v) => v.type === 'ANSWER' && v.value === '') ?? false;
};
/** Check for type `ALL` */
const hasTypeAllValidationError = (value, choiceCount) => {
    // handle `ALL` for choice answers with more than 1 choice
    return typeof choiceCount !== 'number' || value.length !== choiceCount;
};
/** Special case for ImageChoice that does not require all options to be selected for `ALL`, but only at least 1 */
const hasTypeAllValidationErrorWithoutRangeSelection = (value) => {
    return value.length < 1;
};
/** Check for type `AT_LEAST` */
const hasTypeAtLeastValidationError = (value, { min }) => {
    if (typeof min !== 'number') {
        return true; // invalid value or min value
    }
    return value.length < min;
};
/** Check for type `AT_MOST` */
const hasTypeAtMostValidationError = (value, { max }) => {
    if (typeof max !== 'number') {
        return true; // invalid value or max value
    }
    return value.length < 1 || value.length > max;
};
/** Check for type `EXACTLY` */
const hasTypeExactlyValidationError = (value, { min, max }) => {
    if (typeof min !== 'number' || typeof max !== 'number' || min !== max) {
        return true; // invalid value or or min/max value
    }
    return value.length !== min;
};
/** Check for type `RANGE` */
const hasTypeRangeValidationError = (value, { min, max }) => {
    if (typeof min !== 'number' || typeof max !== 'number') {
        return true; // invalid value or min/max value
    }
    return value.length < min || value.length > max;
};
/** returns `null` if valid and error object if not */
const validateRequired = (required, 
/** Id to identify the question */
questionId, value, touched, 
/** Number of answers - only needed for type `ALL` validation of choice answers */
choiceCount = undefined, 
/** Error message to return when the answer checked but left blank */
otherRequiredErrorText = '', 
/** for ImageChoice or other questions where `ALL` does not require all choices to be selected  */
noRangeValidation = false) => {
    if (!touched || !required) {
        return null;
    }
    if (!isResponseValueArray(value)) {
        // invalid value format
        return createValidationError('REQUIRED', required.errorMessage, questionId);
    }
    let hasError = false;
    switch (required.type) {
        case 'ALL':
            if (noRangeValidation) {
                hasError = hasTypeAllValidationErrorWithoutRangeSelection(value);
            }
            else {
                hasError = hasTypeAllValidationError(value, choiceCount);
            }
            break;
        case 'AT_LEAST':
            hasError = hasTypeAtLeastValidationError(value, required);
            break;
        case 'AT_MOST':
            hasError = hasTypeAtMostValidationError(value, required);
            break;
        case 'EXACTLY':
            hasError = hasTypeExactlyValidationError(value, required);
            break;
        case 'RANGE':
            hasError = hasTypeRangeValidationError(value, required);
            break;
    }
    if (hasError) {
        return createValidationError('REQUIRED', required.errorMessage, questionId);
    }
    if (hasEmptyOtherAnswer(value)) {
        // empty other text field
        return createValidationError('REQUIRED', otherRequiredErrorText, questionId);
    }
    return null;
};
/** Predicate to filter visible answers */
const isVisible = (someKindOfAnswer) => someKindOfAnswer.visible !== false;
const requiredCount = ({ answers, answersImage, answersWeighted, fieldSets, isForcedRanking, }) => {
    // if matrix forced ranking is required all the amount of columns and rows needs to be determined and the lowest value is used for validation
    if (isForcedRanking === true) {
        const fieldSetsCount = fieldSets?.filter(isVisible)?.length || 0;
        const answersWeightedCount = answersWeighted?.filter(isVisible)?.length || 0;
        return Math.min(fieldSetsCount, answersWeightedCount);
    }
    return (answers?.filter(isVisible)?.length ??
        answersImage?.filter(isVisible)?.length ??
        fieldSets?.filter(isVisible)?.length ??
        answersWeighted?.filter(isVisible)?.length);
};
/** Validates `valueToValidate` for text-length and returns an `QuestionError` or `null` (valid) */
const checkValueRequired = (question, valueToValidate, touched) => {
    if (!question.required) {
        return null; // questionType does not support validation or question is not touched
    }
    /** Number of answers - only needed for type `ALL` validation of choice answers */
    let total;
    const answerCount = requiredCount(question);
    const fieldSetsCount = question.fieldSets?.filter(isVisible)?.length || 0;
    const answersWeightedCount = question.answersWeighted?.filter(isVisible)?.length ||
        question.answersNested?.filter(isVisible)?.length ||
        0;
    if (question.required.type === 'ALL' && answerCount) {
        total = answerCount;
        if (question.answerOther?.type === 'ANSWER' &&
            question.answerOther?.visible) {
            // answer other choice counts too (comment does not)
            total += 1;
        }
        // `ALL` is not available when NOTA is selected except in Matrix Forced ranking/ this functionality is to match production
        if (total &&
            question.isForcedRanking &&
            (question.answerNoneOfTheAbove?.visible ||
                question.answerNotApplicable?.visible) &&
            answersWeightedCount < fieldSetsCount) {
            total += 1;
        }
        // For Matrix many answers per row.
        if (question.family === 'MATRIX' &&
            question.variant === 'MANY_ANSWERS_CHECKBOX') {
            total = fieldSetsCount;
        }
        // For Matrix single answer rating scale.
        if (question.family === 'RATING_SCALE' &&
            question.variant === 'SINGLE_ANSWER_RADIO') {
            total = 1;
        }
    }
    /** questions where `required.type === ALL` requires all choices to be selected  */
    const allChoicesRequired = ['check_box', 'date_time', 'matrix', 'matrix_dropdown'].includes(question.typeId) || question.variant === 'MANY_ANSWERS_IMAGE';
    return validateRequired(question.required, question.id, valueToValidate, touched, total, question.answerOther?.requiredErrorText, !allChoicesRequired);
};
/** Helper for question-types that only validate require */
const standAloneRequiredValidator = (question, valueToValidate, touched) => {
    const valueError = checkValueRequired(question, valueToValidate, touched);
    return valueError === null ? null : [valueError];
};

/** Array of all validators needed to validate the content of openEnded
 * text inputs (SingleTextbox, `other`, `answerOther`) */
const openEndedValidators = [
    checkValueTextLength,
    checkValueNumericalFormat,
    checkValueDateFormat,
    checkValueEmailFormat,
];
/** Array of all validators needed to validate the
 * contents of multiple text inputs (MultipleTextbox) */
const openEndedMultipleValueValidators = [
    checkValueNumericalFormat,
    checkMultipleValueNumericalSum,
    checkValueTextLength,
    checkValueDateFormat,
    checkValueEmailFormat,
];

const checkSelectedAnswerOtherRequired = (question, valueToValidate, _touched) => {
    return !valueToValidate.length
        ? createValidationError('REQUIRED', question.answerOther?.requiredErrorText, question.id)
        : null;
};
/**
 *
 * Generic validation for components with an single string value (OpenEnded Questions types).
 *
 * E.g. SingleTextbox or CommentBox
 *
 * @returns {QuestionError[] | null} `null` means there is no error - `QuestionError[]` will never be an empty array
 */
const validateRespondentSurveyOpenEndedQuestion = (q, answer, touched) => {
    const valueErrors = [
        checkValueRequired(q, answer, touched),
    ];
    const otherErrors = runValidators(openEndedValidators, q, answer[0]?.value, touched) ?? [];
    return [...valueErrors, ...otherErrors].filter(notNull) ?? null;
};
/**
 *
 * Generic validation for components with multiple response values
 *
 * E.g. MultipleTextbox
 *
 * @returns {QuestionError[] | null} `null` means there is no error - `QuestionError[]` will never be an empty array
 */
const validateRespondentSurveyMultipleOpenEndedQuestion = (q, answers, touched) => {
    const valueErrors = [
        checkValueRequired(q, answers, touched),
    ];
    const answerValues = answers.map((answer) => answer.value);
    const otherErrors = runValidators(openEndedMultipleValueValidators, q, answerValues, touched) ??
        [];
    return [...valueErrors, ...otherErrors].filter(notNull) ?? null;
};
/**
 * Generic validation for components with an single or multiples selections and an (optional) _other_ (`COMMENT` or `ANSWER`) or _None of the Above_ (`NOTA`) option.
 *
 * E.g. Checkboxes, Dropdown or MultipleChoice
 *
 * @returns {QuestionError[] | null} `null` means there is no error - `QuestionError[]` will never be an empty array
 */
const validateRespondentSurveySelectionAndOtherValue = (q, answer, touched) => {
    /** Regular answers, other type 'ANSWER' and 'None of the above' */
    const selectedChoices = answer.filter((a) => !a.type || ['NOTA', 'ANSWER'].includes(a.type));
    /** Any type of 'other' answer */
    const otherColumnArray = answer
        .map((a) => {
        if (Array.isArray(a.value)) {
            const otherToBeChecked = a.value.find(({ type }) => type === 'COMMENT_COLUMN');
            const checkedOther = runValidators(openEndedValidators, q, otherToBeChecked?.value, touched);
            return checkedOther;
        }
        if (a.type === 'COMMENT_COLUMN') {
            const checkedOther = runValidators(openEndedValidators, q, a?.value, touched);
            return checkedOther;
        }
        return null;
    })
        .filter(notNull)
        .reduce((acc, val) => acc.concat(val), []);
    const otherValue = answer.find((a) => a.type && a.type !== 'NOTA');
    const valueErrors = [checkValueRequired(q, selectedChoices, touched)].filter(notNull); // required check for question, if there is no answer
    const hasRequiredAnswerError = valueErrors.length > 0;
    if (!q.answerOther && !q.answerComment) {
        // no other option so we can return here
        return !hasRequiredAnswerError ? null : valueErrors;
    }
    /**
     * Selected Other-Answer requires a non-empty value (regardless of validation or require)
     */
    const selectedAnswerOtherRequiredError = [
        otherValue?.type === 'ANSWER'
            ? checkSelectedAnswerOtherRequired(q, otherValue?.value)
            : null,
    ];
    const returnValueErrors = [
        ...valueErrors,
        ...selectedAnswerOtherRequiredError,
        ...otherColumnArray,
        ...(runValidators(openEndedValidators, q, otherValue?.value, touched, otherValue?.type === 'ANSWER') || []),
    ].filter(notNull);
    return returnValueErrors.length > 0 ? returnValueErrors : null;
};

const singleTextboxQuestionDefinition = {
    typeId: typeId$g,
    fromExternalQuestion: fromExternalQuestion$g,
    toExternalQuestion: toExternalQuestion$g,
    isOfQuestionType: isOfQuestionType$g,
    isCompatible: isCompatible$g,
};

const baseGuards$c = baseQuestionGuards('multiple_textbox', 'OPEN_ENDED', 'MANY_FIELDS');
const isQuestionTypeMultipleTextbox = typeGuard(baseGuards$c);
/**
 * Basic guard to determine question type
 *
 * throws an error when the question does not meet all the type requirements.
 */
function assertQuestionTypeMultipleTextbox(q) {
    return typeAssertion(baseGuards$c)(q);
}

const mapFromExternalQuestion$f = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'multiple_textbox',
        externalQuestion,
        capability,
    });
    assertQuestionTypeMultipleTextbox(questionType);
    return questionType;
};

const typeId$f = 'multiple_textbox';
const fromExternalQuestion$f = (source, capability) => mapFromExternalQuestion$f(source, capability);
const toExternalQuestion$f = (eq, q, capability) => mapInternalDynamicToExternal(eq);
const isCompatible$f = (q) => isCompatible$h(q, typeId$f);
const isOfQuestionType$f = isQuestionTypeMultipleTextbox;

const multipleTextboxQuestionDefinition = {
    typeId: typeId$f,
    fromExternalQuestion: fromExternalQuestion$f,
    toExternalQuestion: toExternalQuestion$f,
    isOfQuestionType: isOfQuestionType$f,
    isCompatible: isCompatible$f,
};

/// Type Guards
const baseGuards$b = baseQuestionGuards('comment_box', 'OPEN_ENDED', 'ESSAY');
const isQuestionTypeCommentBox = typeGuard(baseGuards$b);
/**
 * Basic guard to determine question type
 *
 * throws an error when the question does not meet all the type requirements.
 */
function assertQuestionTypeCommentBox(q) {
    return typeAssertion(baseGuards$b)(q);
}

const mapFromExternalQuestion$e = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'comment_box',
        externalQuestion,
        capability,
    });
    assertQuestionTypeCommentBox(questionType);
    return questionType;
};

const typeId$e = 'comment_box';
const fromExternalQuestion$e = (source, capability) => mapFromExternalQuestion$e(source, capability);
const toExternalQuestion$e = (eq, q, capability) => mapInternalDynamicToExternal(eq);
const isCompatible$e = (q) => isCompatible$h(q, typeId$e);
const isOfQuestionType$e = isQuestionTypeCommentBox;

const commentBoxQuestionDefinition = {
    typeId: typeId$e,
    fromExternalQuestion: fromExternalQuestion$e,
    toExternalQuestion: toExternalQuestion$e,
    isCompatible: isCompatible$e,
    isOfQuestionType: isOfQuestionType$e,
};

/// Type Guards
const baseGuards$a = baseQuestionGuards('multiple_choice', 'MULTIPLE_CHOICE', 'SINGLE_ANSWER_RADIO');
const isQuestionValidationMultipleChoice = isFieldValidation;
const isQuestionTypeMultipleChoice = typeGuard(baseGuards$a);

const mapFromExternalQuestion$d = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'multiple_choice',
        externalQuestion,
        capability,
    });
    return questionType;
};

const typeId$d = 'multiple_choice';
const fromExternalQuestion$d = (source, capability) => mapFromExternalQuestion$d(source, capability);
const toExternalQuestion$d = (eq, q, capability) => mapInternalDynamicToExternal(eq);
const isCompatible$d = (q) => isCompatible$h(q, typeId$d);
const isOfQuestionType$d = isQuestionTypeMultipleChoice;

const multipleChoiceQuestionDefinition = {
    typeId: typeId$d,
    fromExternalQuestion: fromExternalQuestion$d,
    toExternalQuestion: toExternalQuestion$d,
    isCompatible: isCompatible$d,
    isOfQuestionType: isOfQuestionType$d,
};

// TypeGuards and Assertions
const baseGuards$9 = baseQuestionGuards('check_box', 'MULTIPLE_CHOICE', 'MANY_ANSWERS_CHECKBOX');
const isQuestionTypeCheckBox = typeGuard(baseGuards$9);
/**
 * Basic guard to determine question type
 *
 * throws an error when the question does not meet all the type requirements.
 */
function assertQuestionTypeCheckBox(externalQuestion) {
    return typeAssertion(baseGuards$9)(externalQuestion);
}

const mapFromExternalQuestion$c = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'check_box',
        externalQuestion,
        capability,
    });
    assertQuestionTypeCheckBox(questionType);
    return questionType;
};

const typeId$c = 'check_box';
const fromExternalQuestion$c = (source, capability) => mapFromExternalQuestion$c(source, capability);
const toExternalQuestion$c = (eq, q, capability) => mapInternalDynamicToExternal(eq);
const isCompatible$c = (q) => isCompatible$h(q, typeId$c);
const isOfQuestionType$c = isQuestionTypeCheckBox;

const checkBoxQuestionDefinition = {
    typeId: typeId$c,
    fromExternalQuestion: fromExternalQuestion$c,
    toExternalQuestion: toExternalQuestion$c,
    isCompatible: isCompatible$c,
    isOfQuestionType: isOfQuestionType$c,
};

const baseGuards$8 = baseQuestionGuards('dropdown', 'MULTIPLE_CHOICE', 'SINGLE_ANSWER_MENU');
const isQuestionTypeDropdown = typeGuard(baseGuards$8);
/**
 * Basic guard to determine question type
 *
 * throws an error when the question does not meet all the type requirements.
 */
function assertQuestionTypeDropdown(externalQuestion) {
    return typeAssertion(baseGuards$8)(externalQuestion);
}

const mapFromExternalQuestion$b = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'dropdown',
        externalQuestion,
        capability,
    });
    assertQuestionTypeDropdown(questionType);
    return questionType;
};

const typeId$b = 'dropdown';
const fromExternalQuestion$b = (source, capability) => mapFromExternalQuestion$b(source, capability);
const toExternalQuestion$b = (eq, q, capability) => mapInternalDynamicToExternal(eq);
const isCompatible$b = (q) => isCompatible$h(q, typeId$b);
const isOfQuestionType$b = isQuestionTypeDropdown;

const dropdownQuestionDefinition = {
    typeId: typeId$b,
    fromExternalQuestion: fromExternalQuestion$b,
    toExternalQuestion: toExternalQuestion$b,
    isCompatible: isCompatible$b,
    isOfQuestionType: isOfQuestionType$b,
};

const baseGuards$7 = baseQuestionGuards('image_choice', 'MULTIPLE_CHOICE', 'SINGLE_ANSWER_IMAGE');
const isQuestionTypeImageChoice = typeGuard({
    ...baseGuards$7,
    /* To reduce code duplication, we assume the discrepancies between
     * "single answer" and "many answers" question types are limited to "typeId" and "variant".
     */
    typeId: isFixed('image_choice'),
    variant: isEither([
        isFixed('SINGLE_ANSWER_IMAGE'),
        isFixed('MANY_ANSWERS_IMAGE'),
    ]),
});
/**
 * Basic guard to determine question type
 *
 * throws an error when the question does not meet all the type requirements.
 */
function assertQuestionTypeImageChoice(q) {
    return typeAssertion(isQuestionTypeImageChoice)(q);
}

const mapFromExternalQuestion$a = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'image_choice',
        externalQuestion,
        capability,
    });
    assertQuestionTypeImageChoice(questionType);
    return questionType;
};

const typeId$a = 'image_choice';
const fromExternalQuestion$a = (source, capability) => mapFromExternalQuestion$a(source, capability);
const toExternalQuestion$a = (eq, q, capability) => mapInternalDynamicToExternal(eq);
const isCompatible$a = (q) => isCompatible$h(q, typeId$a);
const isOfQuestionType$a = isQuestionTypeImageChoice;

const imageChoiceQuestionDefinition = {
    typeId: typeId$a,
    fromExternalQuestion: fromExternalQuestion$a,
    toExternalQuestion: toExternalQuestion$a,
    isCompatible: isCompatible$a,
    isOfQuestionType: isOfQuestionType$a,
};

const baseGuards$6 = baseQuestionGuards('slider', 'SLIDER', 'SINGLE_SLIDER');
const isQuestionTypeSlider = typeGuard(baseGuards$6);
/**
 * Basic guard to determine question type
 *
 * throws an error when the question does not meet all the type requirements.
 */
function assertQuestionTypeSlider(externalQuestion) {
    return typeAssertion(baseGuards$6)(externalQuestion);
}

const mapFromExternalQuestion$9 = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'slider',
        externalQuestion,
        capability,
    });
    assertQuestionTypeSlider(questionType);
    return questionType;
};

const typeId$9 = 'slider';
const isCompatible$9 = (q) => isCompatible$h(q, typeId$9);
const isOfQuestionType$9 = isQuestionTypeSlider;
const fromExternalQuestion$9 = (source, capability) => mapFromExternalQuestion$9(source, capability);
const toExternalQuestion$9 = (eq, q, capability) => mapInternalDynamicToExternal(eq);

const sliderQuestionDefinition = {
    typeId: typeId$9,
    fromExternalQuestion: fromExternalQuestion$9,
    toExternalQuestion: toExternalQuestion$9,
    isCompatible: isCompatible$9,
    isOfQuestionType: isOfQuestionType$9,
};

const baseStarRatingMatrixGuards = baseQuestionGuards('star_rating', 'MATRIX', 'RATING');
const baseStarRatingScaleGuards = baseQuestionGuards('star_rating', 'RATING_SCALE', 'SINGLE_ANSWER_SYMBOL');
const isQuestionTypeStarRatingMatrix = typeGuard(baseStarRatingMatrixGuards);
const isQuestionTypeStarRatingScale = typeGuard(baseStarRatingScaleGuards);
const isQuestionTypeStarRating = isEither([isQuestionTypeStarRatingMatrix, isQuestionTypeStarRatingScale]);
/**
 * Basic guard to determine question type
 *
 * throws an error when the question does not meet all the type requirements.
 */
function assertQuestionTypeStarRating(externalQuestion) {
    return typeAssertion(isQuestionTypeStarRating)(externalQuestion);
}

const mapFromExternalQuestion$8 = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'star_rating',
        externalQuestion,
        capability,
    });
    assertQuestionTypeStarRating(questionType);
    return questionType;
};

const typeId$8 = 'star_rating';
const isCompatible$8 = (q) => isCompatible$h(q, typeId$8);
const isOfQuestionType$8 = isQuestionTypeStarRating;
const fromExternalQuestion$8 = (source, capability) => mapFromExternalQuestion$8(source, capability);
const toExternalQuestion$8 = (eq, q, capability) => mapInternalDynamicToExternal(eq);

const starRatingQuestionDefinition = {
    typeId: typeId$8,
    fromExternalQuestion: fromExternalQuestion$8,
    toExternalQuestion: toExternalQuestion$8,
    isCompatible: isCompatible$8,
    isOfQuestionType: isOfQuestionType$8,
};

const baseSingleAnswerGuards = baseQuestionGuards('matrix', 'MATRIX', 'SINGLE_ANSWER_RADIO');
const baseManyAnswersGuards = baseQuestionGuards('matrix', 'MATRIX', 'MANY_ANSWERS_CHECKBOX');
const baseRatingScaleGuards = baseQuestionGuards('matrix', 'RATING_SCALE', 'SINGLE_ANSWER_RADIO');
const isQuestionTypeMatrixSingleAnswer = typeGuard(baseSingleAnswerGuards);
const isQuestionTypeMatrixManyAnswers = typeGuard(baseManyAnswersGuards);
const isQuestionTypeMatrixRatingScale = typeGuard(baseRatingScaleGuards);
const isQuestionTypeMatrix = isEither([
    isQuestionTypeMatrixSingleAnswer,
    isQuestionTypeMatrixManyAnswers,
    isQuestionTypeMatrixRatingScale,
]);
/**
 * Basic guard to determine question type
 *
 * throws an error when the question does not meet all the type requirements.
 */
function assertQuestionTypeMatrix(externalQuestion) {
    // TODO: Refactor for better error messages
    if (!isQuestionTypeMatrix(externalQuestion)) {
        throw new Error('Question data does not match any supported question type.');
    }
}

const mapFromExternalQuestion$7 = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'matrix',
        externalQuestion,
        capability,
    });
    assertQuestionTypeMatrix(questionType);
    return questionType;
};

const typeId$7 = 'matrix';
const isCompatible$7 = (q) => isCompatible$h(q, typeId$7);
const isOfQuestionType$7 = isQuestionTypeMatrix;
const fromExternalQuestion$7 = (source, capability) => mapFromExternalQuestion$7(source, capability);
const toExternalQuestion$7 = (eq, q, capability) => mapInternalDynamicToExternal(eq);

// Refine this implementation once we implement this question-type
const matrixRespondentSurveyValidation = (q, answer, touched) => {
    // Required validation for Matrix only work per-row,
    // there is no difference wether there is one or more columns selected,
    // so we can extract the first value (i.e. the columnId) and
    // use`validateRespondentSurveySelectionAndOtherValue` as other validations
    const flattenedValues = answer
        .map(({ value, ...rest }) => {
        // this check is to ensure that the value array is in the correct shape when there is other option per row
        if (Array.isArray(value) &&
            value.find((v) => !!v.value)?.type &&
            value.length > 1) {
            // locating the of the comment column obj
            const commentCol = value.length
                ? value.find((v) => v.type === 'COMMENT_COLUMN')
                : null;
            // locating the index of the comment in the value array
            const foundCommentIndex = commentCol ? value.indexOf(commentCol) : 0;
            // inferring the input index value based on the comment index
            const inputIndex = foundCommentIndex === 0 ? 1 : 0;
            // structuring the value array so it is in the appropriate shape for the validation
            const valueArray = [
                {
                    id: value[foundCommentIndex].id,
                    type: value[foundCommentIndex].type,
                    value: value[foundCommentIndex].value,
                },
                {
                    id: value[inputIndex].id,
                    value: value[inputIndex].value,
                },
            ];
            return {
                ...rest,
                value: valueArray,
            };
        }
        if (Array.isArray(value) && value.find((v) => !!v.value)?.type) {
            // this is to make sure the shape is correct for one question per row as it's a nested value and the type isn't getting logged in rest
            return {
                ...rest,
                type: value.find((v) => !!v.value)?.type,
                value: value.find((v) => !!v.value)?.value,
            };
        }
        return {
            ...rest,
            value,
        };
    })
        .filter((a) => !!a.value);
    return validateRespondentSurveySelectionAndOtherValue(q, flattenedValues, touched);
};

const matrixQuestionDefinition = {
    typeId: typeId$7,
    fromExternalQuestion: fromExternalQuestion$7,
    toExternalQuestion: toExternalQuestion$7,
    isOfQuestionType: isOfQuestionType$7,
    isCompatible: isCompatible$7,
};

const baseGuards$5 = baseQuestionGuards('matrix_dropdown', 'MATRIX', 'MENU');
const isQuestionTypeMatrixDropdown = typeGuard(baseGuards$5);
/**
 * Basic guard to determine question type
 *
 * throws an error when the question does not meet all the type requirements.
 */
function assertQuestionTypeMatrixDropdown(externalQuestion) {
    return typeAssertion(baseGuards$5)(externalQuestion);
}

const mapFromExternalQuestion$6 = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'matrix_dropdown',
        externalQuestion,
        capability,
    });
    assertQuestionTypeMatrixDropdown(questionType);
    return questionType;
};

const typeId$6 = 'matrix_dropdown';
const fromExternalQuestion$6 = (source, capability) => mapFromExternalQuestion$6(source, capability);
const toExternalQuestion$6 = (eq, q, capability) => mapInternalDynamicToExternal(eq);
const isCompatible$6 = (q) => isCompatible$h(q, typeId$6);
const isOfQuestionType$6 = isQuestionTypeMatrixDropdown;

// Refine this implementation once we implement this question-type
const matrixDropdownRespondentSurveyValidation = (q, userAnswers, touched) => {
    // Required validation for MatrixDropdown only work per-row,
    // there is no difference wether there is one or more columns selected,
    // so we can extract the first value (i.e. the columnId) and
    // use`validateRespondentSurveySelectionAndOtherValue` as other validations
    const choicesPerRow = q.answersNested?.filter((answer) => answer.visible).length || 0;
    const flattenedValues = userAnswers
        .map(({ value, ...rest }) => {
        // Comment answers come in as an array to match the question answer shape. This variable pulles out the appropriate value depending on if the answer is an question answer or comment answer.
        const val = rest.type && rest.type === 'COMMENT' ? value[0] : value;
        // For required validations all columns in a row are required to have values for the row to be considered valid.
        // If a row is valid we do not need to include the array of values, only a single non-empty string value. We are simply using 'valid' as the value to signify a flattened row is valid.
        const nestedValue = Array.isArray(val) && val.length === choicesPerRow ? 'valid' : null;
        const commentValue = !nestedValue
            ? val.value
            : null;
        return {
            ...rest,
            value: nestedValue ?? commentValue,
        };
    })
        .filter((a) => !!a.value);
    return validateRespondentSurveySelectionAndOtherValue(q, flattenedValues, touched);
};

const matrixDropdownQuestionDefinition = {
    typeId: typeId$6,
    fromExternalQuestion: fromExternalQuestion$6,
    toExternalQuestion: toExternalQuestion$6,
    isOfQuestionType: isOfQuestionType$6,
    isCompatible: isCompatible$6,
};

const baseGuards$4 = baseQuestionGuards('ranking', 'RANKING', 'SINGLE_ANSWER_RADIO');
const isQuestionTypeRanking = typeGuard(baseGuards$4);
/**
 * Basic guard to determine question type
 *
 * throws an error when the question does not meet all the type requirements.
 */
function assertQuestionTypeRanking(externalQuestion) {
    return typeAssertion(baseGuards$4)(externalQuestion);
}

const mapFromExternalQuestion$5 = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'ranking',
        externalQuestion,
        capability,
    });
    assertQuestionTypeRanking(questionType);
    return questionType;
};

const typeId$5 = 'ranking';
const isCompatible$5 = (q) => isCompatible$h(q, typeId$5);
const isOfQuestionType$5 = isQuestionTypeRanking;
const fromExternalQuestion$5 = (source, capability) => mapFromExternalQuestion$5(source, capability);
const toExternalQuestion$5 = (eq, q, capability) => mapInternalDynamicToExternal(eq);

const rankingQuestionDefinition = {
    typeId: typeId$5,
    fromExternalQuestion: fromExternalQuestion$5,
    toExternalQuestion: toExternalQuestion$5,
    isCompatible: isCompatible$5,
    isOfQuestionType: isOfQuestionType$5,
};

/// Type Guards
const baseGuards$3 = baseQuestionGuards('nps', 'RATING_SCALE', 'NPS');
const isQuestionValidationNps = isFieldValidation;
const isQuestionTypeNps = typeGuard(baseGuards$3);

const mapFromExternalQuestion$4 = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'nps',
        externalQuestion,
        capability,
    });
    return questionType;
};

const typeId$4 = 'nps';
const fromExternalQuestion$4 = (source, capability) => mapFromExternalQuestion$4(source, capability);
const toExternalQuestion$4 = (eq, q, capability) => mapInternalDynamicToExternal(eq);
const isCompatible$4 = (q) => isCompatible$h(q, typeId$4);
const isOfQuestionType$4 = isQuestionTypeNps;

const npsQuestionDefinition = {
    typeId: typeId$4,
    fromExternalQuestion: fromExternalQuestion$4,
    toExternalQuestion: toExternalQuestion$4,
    isCompatible: isCompatible$4,
    isOfQuestionType: isOfQuestionType$4,
};

/// Type Guards
const baseDateTimeGuards = baseQuestionGuards('date_time', 'DATETIME', 'DATETIME');
const baseDateGuards = baseQuestionGuards('date_time', 'DATETIME', 'DATE');
const baseTimeGuards = baseQuestionGuards('date_time', 'DATETIME', 'TIME');
const isQuestionTypeDateTimeDateAndTime = typeGuard(baseDateTimeGuards);
const isQuestionTypeDateTimeDate = typeGuard(baseDateGuards);
const isQuestionTypeDateTimeTime = typeGuard(baseTimeGuards);
const isQuestionTypeDateTime = isEither([
    isQuestionTypeDateTimeDateAndTime,
    isQuestionTypeDateTimeDate,
    isQuestionTypeDateTimeTime,
]);

const mapFromExternalQuestion$3 = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'date_time',
        externalQuestion,
        capability,
    });
    return questionType;
};

const typeId$3 = 'date_time';
const fromExternalQuestion$3 = (source, capability) => mapFromExternalQuestion$3(source, capability);
const toExternalQuestion$3 = (eq, q, capability) => mapInternalDynamicToExternal(eq);
const isCompatible$3 = (q) => isCompatible$h(q, typeId$3);
const isOfQuestionType$3 = isQuestionTypeDateTime;

/**
 * Default error message token that need to be overwritten from consumers in
 * questions-ui to allow l10n.
 *
 * This is because other QuestionTypes' error messages are coming from the GraphAPI,
 * but the ones below are client-side only.
 */
const errorMessageTokens = {
    /** Incorrect or missing `date` input for `DATE_INTL`. To be replaced with localized: _Please provide date in dd/mm/yyyy format._ */
    dateIntl: 'ERROR_PROVIDE_DATE_INTL_FORMAT',
    /** Incorrect or missing `date` input for `DATE_US`. To be replaced with localized: _Please provide date in mm/dd/yyyy format._ */
    dateUs: 'ERROR_PROVIDE_DATE_US_FORMAT',
    /** Incorrect or missing `minute` input. To be replaced with localized: _Please enter hrs from 0 to 23._ */
    hour: 'ERROR_ENTER_HOURS',
    /** Incorrect or missing `minute` input. To be replaced with localized: _Please enter mins from 0 to 59.._ */
    minute: 'ERROR_ENTER_MINUTES',
    /** Incorrect or missing `period` selection. To be replaced with localized: _Please select AM/PM._ */
    period: 'ERROR_SELECT_PERIOD',
    /** Year is out of range. To be replaced with localized: _Please provide year from 1900 to 9999._ */
    yearOutOfRange: 'ERROR_YEAR_OUT_OF_RANGE',
};
const hasValidTime = ({ hour = '', minute = '', period, }) => hour.length > 0 &&
    minute.length > 0 &&
    ['AM', 'PM', '-'].includes(period ?? '');
/** Convert DateTime Answers to simple string representation for require check */
const dateTimeAnswerPayloadToString = ({ date, hour, minute, period }, variant) => {
    if (variant === 'DATE') {
        return date ?? '';
    }
    if (!hasValidTime({ hour, minute, period })) {
        // short-circuit to show error if time is not valid
        return '';
    }
    if (variant === 'TIME') {
        return `${hour}:${minute}${period}`;
    }
    if (!date) {
        return ''; // time without date is invalid
    }
    return `${date} ${hour}:${minute}${period}`;
};
/**
 * Flattens the answer values to string,
 * i.e. converts `ResponseValue<DateTimeAnswerPayload>[]` to `ResponseValue<string>[]`
 */
const stringifyAnswerValues = (answers, variant) => answers
    .map((a) => ({
    ...a,
    value: dateTimeAnswerPayloadToString(a.value, variant),
}))
    .filter((a) => a.value !== ''); // remove empty or invalid answers
/** Validation logic for the time only */
const validateTimeFormat = (id, fieldId, type, hour, minute, period) => {
    const errors = [];
    const hourParsed = parseInt(hour || '', 10);
    const minParsed = parseInt(minute || '', 10);
    if (Number.isNaN(hourParsed) || hourParsed < 0 || hourParsed > 23) {
        errors.push(createValidationError(type, errorMessageTokens.hour, id, fieldId));
    }
    if (Number.isNaN(minParsed) || minParsed < 0 || minParsed > 59) {
        errors.push(createValidationError(type, errorMessageTokens.minute, id, fieldId));
    }
    if (period !== 'AM' && period !== 'PM') {
        errors.push(createValidationError(type, errorMessageTokens.period, id, fieldId));
    }
    return errors;
};
/**
 * Validates a single DateTime answer
 *
 * @returns `null` if valid and error object if not
 */
const checkValueFormat = (q, { date, hour, minute, period }, touched, fieldId) => {
    let errors = [];
    const { id, validation: { type }, variant, } = q;
    if (variant === 'DATE' || variant === 'DATETIME') {
        if (checkValueDateFormat(q, date, touched, true)) {
            errors.push(createValidationError(type, type === 'DATE_INTL'
                ? errorMessageTokens.dateIntl
                : errorMessageTokens.dateUs, id, fieldId));
        }
        else if (!isYearValid(q, date)) {
            errors.push(createValidationError(type, errorMessageTokens.yearOutOfRange, id, fieldId));
        }
    }
    if (variant === 'TIME' || variant === 'DATETIME') {
        errors = [
            ...errors,
            ...validateTimeFormat(id, fieldId, type, hour, minute, period),
        ];
    }
    return errors.length > 0 ? errors : null;
};
/**
 * Creates the validation function for DateTime component.
 *
 * This validation is unique as it needs additional error messages that are
 * not returned in the GraphAPI response, and therefore are provided as tokens
 * (see `errorMessageTokens`) here to get replaced in questions-ui as this it
 * the only package in SMQuestions with l18n support and it is not worth the
 * setup and maintenance overhead to enable it here just for that.
 *
 * @returns {QuestionError[] | null} `null` means there is no error - `QuestionError[]` will never be an empty array
 */
const dateTimeRespondentSurveyValidation = (q, answers, touched) => {
    const simplifiedAnswers = stringifyAnswerValues(answers, q.variant);
    const valueErrors = [
        checkValueRequired(q, simplifiedAnswers, touched),
    ];
    const dateFormatErrors = answers.reduce((acc, curr) => {
        const errors = checkValueFormat(q, curr.value, touched, curr.id);
        if (errors === null) {
            return acc;
        }
        return [...acc, ...errors];
    }, []);
    const errors = [...valueErrors, ...dateFormatErrors].filter(notNull);
    return errors.length > 0 ? errors : null;
};

const dateTimeQuestionDefinition = {
    typeId: typeId$3,
    fromExternalQuestion: fromExternalQuestion$3,
    toExternalQuestion: toExternalQuestion$3,
    isCompatible: isCompatible$3,
    isOfQuestionType: isOfQuestionType$3,
};

const baseGuards$2 = baseQuestionGuards('image_presentation', 'PRESENTATION', 'IMAGE');
const isQuestionTypeImagePresentation = typeGuard(baseGuards$2);
/**
 * Basic guard to determine question type
 *
 * throws an error when the question does not meet all the type requirements.
 */
function assertQuestionTypeImagePresentation(externalQuestion) {
    return typeAssertion(baseGuards$2)(externalQuestion);
}

const mapFromExternalQuestion$2 = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'image_presentation',
        externalQuestion,
        capability,
    });
    assertQuestionTypeImagePresentation(questionType);
    return questionType;
};

const typeId$2 = 'image_presentation';
const fromExternalQuestion$2 = (source, capability) => mapFromExternalQuestion$2(source, capability);
const toExternalQuestion$2 = (eq, q, capability) => mapInternalDynamicToExternal(eq);
const isCompatible$2 = (q) => isCompatible$h(q, typeId$2);
const isOfQuestionType$2 = isQuestionTypeImagePresentation;

const imagePresentationQuestionDefinition = {
    typeId: typeId$2,
    fromExternalQuestion: fromExternalQuestion$2,
    toExternalQuestion: toExternalQuestion$2,
    isCompatible: isCompatible$2,
    isOfQuestionType: isOfQuestionType$2,
};

const baseGuards$1 = baseQuestionGuards('text_presentation', 'PRESENTATION', 'TEXT');
const isQuestionTypeTextPresentation = typeGuard(baseGuards$1);
/**
 * Basic guard to determine question type
 *
 * throws an error when the question does not meet all the type requirements.
 */
function assertQuestionTypeTextPresentation(externalQuestion) {
    return typeAssertion(baseGuards$1)(externalQuestion);
}

const mapFromExternalQuestion$1 = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'text_presentation',
        externalQuestion,
        capability,
    });
    assertQuestionTypeTextPresentation(questionType);
    return questionType;
};

const typeId$1 = 'text_presentation';
const fromExternalQuestion$1 = (source, capability) => mapFromExternalQuestion$1(source, capability);
const toExternalQuestion$1 = (eq, q, capability) => mapInternalDynamicToExternal(eq);
const isCompatible$1 = (q) => isCompatible$h(q, typeId$1);
const isOfQuestionType$1 = isQuestionTypeTextPresentation;

const textPresentationQuestionDefinition = {
    typeId: typeId$1,
    fromExternalQuestion: fromExternalQuestion$1,
    toExternalQuestion: toExternalQuestion$1,
    isCompatible: isCompatible$1,
    isOfQuestionType: isOfQuestionType$1,
};

const baseGuards = baseQuestionGuards('file_upload', 'FILE_UPLOAD', 'FILE_UPLOAD');
const isQuestionTypeFileUpload = typeGuard({
    ...baseGuards,
    typeId: isFixed('file_upload'),
    variant: isEither([isFixed('FILE_UPLOAD')]),
});
/**
 * Basic guard to determine question type
 *
 * throws an error when the question does not meet all the type requirements.
 */
function assertQuestionTypeFileUploads(q) {
    return typeAssertion(isQuestionTypeFileUpload)(q);
}

const mapFromExternalQuestion = (externalQuestion, capability) => {
    const questionType = mapDynamic({
        typeId: 'file_upload',
        externalQuestion,
        capability,
    });
    assertQuestionTypeFileUploads(questionType);
    return questionType;
};

const typeId = 'file_upload';
const fromExternalQuestion = (source, capability) => mapFromExternalQuestion(source, capability);
const toExternalQuestion = (eq, q, capability) => mapInternalDynamicToExternal(eq);
const isCompatible = (q) => isCompatible$h(q, typeId);
const isOfQuestionType = isQuestionTypeFileUpload;

const fileUploadQuestionDefinition = {
    typeId,
    fromExternalQuestion,
    toExternalQuestion,
    isCompatible,
    isOfQuestionType,
};

const questionTypeDefinitionModules = [
    singleTextboxQuestionDefinition,
    multipleTextboxQuestionDefinition,
    commentBoxQuestionDefinition,
    multipleChoiceQuestionDefinition,
    checkBoxQuestionDefinition,
    imageChoiceQuestionDefinition,
    dropdownQuestionDefinition,
    sliderQuestionDefinition,
    starRatingQuestionDefinition,
    matrixQuestionDefinition,
    matrixDropdownQuestionDefinition,
    npsQuestionDefinition,
    fileUploadQuestionDefinition,
    dateTimeQuestionDefinition,
    rankingQuestionDefinition,
    imagePresentationQuestionDefinition,
    textPresentationQuestionDefinition,
];
const findByType = (typeId) => (qd) => qd.typeId === typeId;
/** Find the QuestionTypeDefinitionModule for `typeId` */
const pickQuestionTypeDefinition = (typeId) => {
    const res = questionTypeDefinitionModules.find(findByType(typeId));
    if (res === undefined) {
        throw new Error(`Unable to find module definition for type ${typeId}`);
    }
    return res;
};
/** Find a compatible QuestionTypeDefinitionModule for an external question */
const selectQuestionTypeDefinition = (q) => questionTypeDefinitionModules.find(({ isCompatible }) => isCompatible(q)) ??
    null;

/// Aggregate Response Guard Helpers
/// GQL Types that will be generated soon.
const isAggregateResponseHeaderItem = typeGuard({
    id: isString,
    label: isString,
    weight: isNullable(isNumber),
});
const isAggregateResponseHeader = typeGuard({
    id: isString,
    items: isArrayOf(isAggregateResponseHeaderItem),
});
const isAggregateResponseCell = typeGuard({
    rowItem: isNullable(isAggregateResponseHeaderItem),
    columnItem: isNullable(isAggregateResponseHeaderItem),
    groupItem: isNullable(isAggregateResponseHeaderItem),
    percentage: isOptional(isNumber),
    total: isNumber,
});
const aggregateResponseBasicStatDimension = [
    'ROWS',
    'COLUMNS',
    'GROUPS',
];
const isAggregateResponseBasicStats = typeGuard({
    across: isNullable(isOneOf(aggregateResponseBasicStatDimension)),
    row: isNullable(isAggregateResponseHeaderItem),
    column: isNullable(isAggregateResponseHeaderItem),
    group: isNullable(isAggregateResponseHeaderItem),
});
const isAggregateResponseOtherAnswerItem = typeGuard({
    text: isString,
});
const isAggregateResponseOtherAnswer = typeGuard({
    title: isString,
    totalCount: isNumber,
    items: isArrayOf(isAggregateResponseOtherAnswerItem),
});
// One dimensional aggregate response
const isOneDimensionalAggregateResponseCell = typeGuard({
    rowItem: isAggregateResponseHeaderItem,
    columnItem: isNull,
    groupItem: isNull,
    percentage: isOptional(isNumber),
    total: isNumber,
});
const isOneDimensionalAggregateResponse = typeGuard({
    id: isString,
    type: isFixed('ONE_D'),
    rowHeader: isAggregateResponseHeader,
    columnHeader: isNull,
    groupHeader: isNull,
    data: isArrayOf(isOneDimensionalAggregateResponseCell),
    otherAnswers: isOptional(isAggregateResponseOtherAnswer),
    stats: isArrayOf(isAggregateResponseBasicStats),
});
// Two dimensional aggregate response
const isTwoDimensionalAggregateResponseCell = typeGuard({
    rowItem: isAggregateResponseHeaderItem,
    columnItem: isAggregateResponseHeaderItem,
    groupItem: isNull,
    percentage: isOptional(isNumber),
    total: isNumber,
});
const isTwoDimensionalAggregateResponse = typeGuard({
    id: isString,
    type: isFixed('TWO_D'),
    rowHeader: isAggregateResponseHeader,
    columnHeader: isAggregateResponseHeader,
    groupHeader: isNull,
    data: isArrayOf(isTwoDimensionalAggregateResponseCell),
    otherAnswers: isOptional(isAggregateResponseOtherAnswer),
    stats: isArrayOf(isAggregateResponseBasicStats),
});
// Three dimensional aggregate response
const isThreeDimensionalAggregateResponseCell = typeGuard({
    rowItem: isAggregateResponseHeaderItem,
    columnItem: isAggregateResponseHeaderItem,
    groupItem: isAggregateResponseHeaderItem,
    percentage: isOptional(isNumber),
    total: isNumber,
});
const isThreeDimensionalAggregateResponse = typeGuard({
    id: isString,
    type: isFixed('THREE_D'),
    rowHeader: isAggregateResponseHeader,
    columnHeader: isAggregateResponseHeader,
    groupHeader: isAggregateResponseHeader,
    data: isArrayOf(isThreeDimensionalAggregateResponseCell),
    otherAnswers: isOptional(isAggregateResponseOtherAnswer),
    stats: isArrayOf(isAggregateResponseBasicStats),
});
// Transformed type guards
const isOneDimensionalChartData = typeGuard({
    id: isString,
    rowID: isString,
    rowLabel: isString,
    total: isNumber,
    percentage: isOptional(isNumber),
});
const isTwoDimensionalData = typeGuard({
    columnID: isString,
    columnLabel: isString,
    data: isArrayOf(isOneDimensionalChartData),
});
const isThreeDimensionalData = typeGuard({
    groupID: isString,
    groupLabel: isString,
    data: isArrayOf(isTwoDimensionalData),
});

/** Converts the external `family` and `variant` to the internally used `TypeId` */
const getTypeId = ({ family, variant, }) => {
    switch (family) {
        case 'OPEN_ENDED':
            switch (variant) {
                case 'SINGLE_LINE':
                    return 'single_textbox';
                case 'ESSAY':
                    return 'comment_box';
                case 'MANY_FIELDS':
                    return 'multiple_textbox';
                default:
                    return null;
            }
        case 'MULTIPLE_CHOICE':
            switch (variant) {
                case 'SINGLE_ANSWER_RADIO':
                    return 'multiple_choice';
                case 'MANY_ANSWERS_CHECKBOX':
                    return 'check_box';
                case 'SINGLE_ANSWER_IMAGE':
                case 'MANY_ANSWERS_IMAGE':
                    return 'image_choice';
                case 'SINGLE_ANSWER_MENU':
                    return 'dropdown';
                default:
                    return null;
            }
        case 'SLIDER':
            switch (variant) {
                case 'SINGLE_SLIDER':
                    return 'slider';
                default:
                    return null;
            }
        case 'RATING_SCALE':
            switch (variant) {
                case 'SINGLE_ANSWER_SYMBOL':
                    return 'star_rating';
                case 'SINGLE_ANSWER_RADIO':
                    return 'matrix';
                case 'NPS':
                    return 'nps';
                default:
                    return null;
            }
        case 'MATRIX':
            switch (variant) {
                case 'SINGLE_ANSWER_RADIO':
                case 'MANY_ANSWERS_CHECKBOX':
                    return 'matrix';
                case 'MENU':
                    return 'matrix_dropdown';
                case 'RATING':
                    return 'star_rating';
                default:
                    return null;
            }
        case 'RANKING':
            switch (variant) {
                case 'SINGLE_ANSWER_RADIO':
                    return 'ranking';
                default:
                    return null;
            }
        case 'DATETIME':
            switch (variant) {
                case 'DATETIME':
                case 'DATE':
                case 'TIME':
                    return 'date_time';
                default:
                    return null;
            }
        case 'PRESENTATION':
            switch (variant) {
                case 'IMAGE':
                    return 'image_presentation';
                case 'TEXT':
                    return 'text_presentation';
                default:
                    return null;
            }
        case 'FILE_UPLOAD': {
            switch (variant) {
                case 'FILE_UPLOAD':
                    return 'file_upload';
                default:
                    return null;
            }
        }
        default:
            return null;
    }
};

const isExternalQuestionWithTypeId = typeGuard({
    typeId: isString,
});
function assertExternalQuestionWithIds(u) {
    return typeAssertion({ typeId: isString, id: isString })(u);
}

export { SENTIMENTS, aggregateResponseBasicStatDimension, assertExternalQuestionWithIds, assertQuestionTypeCheckBox, assertQuestionTypeCommentBox, assertQuestionTypeDropdown, assertQuestionTypeFileUploads, assertQuestionTypeImageChoice, assertQuestionTypeImagePresentation, assertQuestionTypeMatrix, assertQuestionTypeMatrixDropdown, assertQuestionTypeMultipleTextbox, assertQuestionTypeRanking, assertQuestionTypeSingleTextbox, assertQuestionTypeSlider, assertQuestionTypeStarRating, assertQuestionTypeTextPresentation, baseQuestionGuards, checkBoxQuestionDefinition, validateRespondentSurveySelectionAndOtherValue as checkBoxRespondentSurveyValidation, commentBoxQuestionDefinition, validateRespondentSurveyOpenEndedQuestion as commentBoxRespondentSurveyValidation, dateTimeQuestionDefinition, dateTimeRespondentSurveyValidation, dropdownQuestionDefinition, validateRespondentSurveySelectionAndOtherValue as dropdownRespondentSurveyValidation, fileUploadQuestionDefinition, standAloneRequiredValidator as fileUploadRespondentSurveyValidation, fixedWidthOptions, getTypeId, imageChoiceQuestionDefinition, standAloneRequiredValidator as imageChoiceRespondentSurveyValidation, imagePresentationQuestionDefinition, isAggregateResponseCell, isAggregateResponseHeader, isAggregateResponseHeaderItem, isExternalQuestionWithTypeId, isOneDimensionalAggregateResponse, isOneDimensionalChartData, isQuestionError, isQuestionType, isQuestionTypeCheckBox, isQuestionTypeCommentBox, isQuestionTypeDateTime, isQuestionTypeDropdown, isQuestionTypeFileUpload, isQuestionTypeImageChoice, isQuestionTypeImagePresentation, isQuestionTypeMatrix, isQuestionTypeMatrixDropdown, isQuestionTypeMultipleChoice, isQuestionTypeMultipleTextbox, isQuestionTypeNps, isQuestionTypeRanking, isQuestionTypeSingleTextbox, isQuestionTypeSlider, isQuestionTypeStarRating, isQuestionTypeTextPresentation, isQuestionValidationMultipleChoice, isQuestionValidationNps, isThreeDimensionalAggregateResponse, isThreeDimensionalData, isTwoDimensionalAggregateResponse, isTwoDimensionalData, matrixDropdownQuestionDefinition, matrixDropdownRespondentSurveyValidation, matrixQuestionDefinition, matrixRespondentSurveyValidation, multipleChoiceQuestionDefinition, validateRespondentSurveySelectionAndOtherValue as multipleChoiceRespondentSurveyValidation, multipleTextboxQuestionDefinition, validateRespondentSurveyMultipleOpenEndedQuestion as multipleTextboxRespondentSurveyValidation, npsQuestionDefinition, standAloneRequiredValidator as npsRespondentSurveyValidation, pickQuestionTypeDefinition, questionTypeFamilies, questionTypeIds, questionTypeLayoutPlacements, questionTypeMultipleChoiceLayouts, questionTypeRequiredPredicateTypes, questionTypeSkipLogicDestinationTypes, questionTypeSkipLogicTypes, questionTypeSortingKinds, questionTypeValidationTypes, questionTypeVariants, rankingQuestionDefinition, standAloneRequiredValidator as rankingRespondentSurveyValidation, ratingScaleSymbols, relativeWidthOptions, selectQuestionTypeDefinition, singleTextboxQuestionDefinition, validateRespondentSurveyOpenEndedQuestion as singleTextboxRespondentSurveyValidation, sliderQuestionDefinition, validateRespondentSurveySelectionAndOtherValue as sliderRespondentSurveyValidation, starRatingQuestionDefinition, validateRespondentSurveySelectionAndOtherValue as starRatingRespondentSurveyValidation, textPresentationQuestionDefinition };
//# sourceMappingURL=index.js.map
